/*
 * Application form scripts
 *
 * - Form behaviour
 * - Navigation between form stages
 * - Form submissions
 * - Validation
 * - Error handling and notifications
 * - ...
 *
 * Note: not to be confused with `app.js` which is the file that gets fed into
 *       Webpack and includes -- among other files -- this file.
 */

/**
 * Privacy consent reminder
 */
import {
  addressInput,
  bindDobInputs,
  displayErrorMessage,
  getGoogleMapsAutoComplete,
  handleAddEventSmartlook,
  saveApplication
} from "./common";
import {
  APPLICATION_NOTES_RULES,
  ASSETS_RULES, BASIC_RULES,
  BUSINESS_RULES,
  EXPENSES_RULES,
  INCOME_RULES,
  INDIVIDUAL_COMMERCIAL_RULES,
  INDIVIDUAL_RESIDENTIAL_RULES,
  INDIVIDUAL_RULES,
  LOAN_RULES,
  PRODUCT_RULES,
  UPLOAD_DOCUMENT_RULES,
  generateRequiredRule
} from "./rules";
import { validate } from "./validate";
import {
  APPLICANT_TYPE_COMPANY,
  APPLICANT_TYPE_INDIVIDUAL,
  APPLICANT_TYPE_PARTNERSHIP,
  APPLICANT_TYPE_TRUST,
  APPLICATION_RESIDENTIAL,
  INCOME_TYPE_SELF_EMPLOYED,
  MINIMUM_BANK_STATEMENT,
  INCOME_OTHER_TYPES_GROUP,
  OWNERSHIP_LIVES_WITH_RELATIVES,
  OWNERSHIP_MORTGAGE,
  OWNERSHIP_OWNS_OUTRIGHT,
  OWNERSHIP_RENTS,
  INCOME_PERMANENT_TYPES_GROUP,
  POSTAL_ADDRESS_MANUAL,
  PRODUCT_DETAIL_ALL,
  PRODUCT_DETAIL_PARTIAL,
  PRODUCT_STATUS_DEMO,
  PRODUCT_STATUS_USED,
  APPLICATION_COMMERCIAL,
  INCOME_INVESTMENT_PROPERTY,
  APPLICATION_CAR,
  ASSET_CONDITION_USED,
  APPLICATION_STOCK_FINANCE
} from "./constants";

let startTimeCreateCommercialStep = 0;

function privacyConsentReminder() {
  if ($('input[name="previously_submitted"]').val() === '1') {
    $('input[name="previously_submitted"]').val('0');
    $('#submitButton').html('Consent &amp; Submit');

    // Alert user of need to resubmit application
    Swal.fire({
      title: 'Privacy consent',
      type: 'warning',
      text:
        'As the application has changed since submission, remember to resubmit the application ' +
        'after completing your changes.',
      showConfirmButton: true,
      customClass: {
        title: 'sf-text-orange',
        confirmButton: 'btn btn-info'
      },
      buttonsStyling: false
    });
  }
}

/**
 * Checks if data parameter is a valid json object/string
 *
 * @return {boolean}
 */
function IsJsonString(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }

  return true;
}

/**
 * Get data from the form, used to check if the form is diry
 * Dependency for isDirty() Function
 *
 * @param value
 * @returns {*}
 */
function extractFormData(value) {
  let inputValue = value;
  // check input value is a json value, if so extract data
  if (IsJsonString(value)) {
    let jsonValue = JSON.parse(value);
    inputValue = jsonValue;

    // if an array override input value with
    // array property value
    if (Array.isArray(jsonValue)) {
      jsonValue = jsonValue[0];
      if (jsonValue.hasOwnProperty('value')) {
        inputValue = jsonValue.value;
      }
    }
  }

  return inputValue;
}

/**
 * Compares the current value on the form from the
 * input value of a string or a number
 *
 * @param defaultValue
 * @param inputValue
 * @returns {boolean}
 */
function compareIsDirtyStringNumberData(defaultValue, inputValue) {
  // compare string values
  if (
    (isNaN(defaultValue) && isNaN(inputValue)) ||
    ((typeof defaultValue === 'string' || defaultValue instanceof String) &&
      (typeof inputValue === 'string' || inputValue instanceof String))
  ) {
    // if the saved value is equal to the input value
    if (defaultValue !== inputValue) {
      return true;
    }
  } else {
    // compare number values
    if (parseInt(defaultValue) !== parseInt(inputValue)) {
      return true;
    }
  }

  return false;
}

/**
 * Compares the current value on the form from the select input,
 * radio or checkbox value
 *
 * @param element
 * @returns {boolean}
 */
function compareIsDirtySelectRadioCheckbox(element) {
  if (element.type === 'radio' || element.type === 'checkbox') {
    if (element.defaultChecked !== element.checked) {
      return true;
    }
  } else if (
    element.type === 'select-one' ||
    element.type === 'select-multiple'
  ) {
    for (let x = 0; x < element.length; x++) {
      if (element.options[x].selected !== element.options[x].defaultSelected) {
        return true;
      }
    }
  }

  return false;
}

/**
 * See if a form has any changes
 *
 * @return boolean
 */
function isDirty(form) {
  let hasChanges = false;

  form.find(':input:not(:button):not([type=hidden])').each(function () {
    // text, textarea and hidden inputs
    if (
      this.type === 'text' ||
      this.type === 'textarea' ||
      this.type === 'hidden'
    ) {
      const inputValue = extractFormData(this.value);
      // compare string values
      hasChanges =
        hasChanges ||
        compareIsDirtyStringNumberData(this.defaultValue, inputValue);
    } else {
      // select, radio and checkbox
      hasChanges = hasChanges || compareIsDirtySelectRadioCheckbox(this);
    }

    return null;
  });

  return hasChanges;
}

/**
 * Confirm making changes after application has submitted
 *
 * @return boolean
 */
function confirmPrivacyNullification() {
  // Not previously submitted (new application)
  if ($('input[name="previously_submitted"]').val() !== '1') {
    return true;
  }

  // Ask for confirmation
  if (
    !confirm(
      'Making changes after the application has been submitted will require a new privacy consent ' +
      'to be sent out. Would you like to continue with the above changes?'
    )
  ) {
    return false;
  }

  // Clear previously submitted status
  $('input[name="previously_submitted"]').val('0');

  return true;
}

let lastSuccessfulAbnLookup = null;
let lastSuccessfulAcnLookup = null;

/**
 * Look up an ABN and display the results
 *
 * @param abn
 * @param lengthCheck
 */
function abnLookup(abn, lengthCheck) {
  if (abn === undefined) {
    return;
  }

  abn = abn.replace(/[^0-9]/g, '');

  if (abn.length && abn === lastSuccessfulAbnLookup) {
    return;
  }
  lastSuccessfulAbnLookup = null;

  $('#business_name_text').html('');
  $('#business_name').html('');
  $('#applicant_type_text').html('');
  $('#applicant_type').val('');
  $('.progress-status').hide();

  if (abn.length === 0 || abn.length !== 11) {
    $('.business_acn_div').hide();
    $('.partnersDiv').hide();
    if (abn.length && lengthCheck) {
      $('#abn_number-err').css('display', 'block !important');
      $('#abn_number-err').html('ABN number must be 11 digits long');
    }
    return;
  }

  $('#abn_number-err').html('');
  // Looking up ABN
  const data = {
    abn,
    application_id: $('.application_id').val()
  };

  window.httpPost('/abnLookup', data, {
    success(response) {
      const dataResponse = response.data
      $('.js-display-errors').html('');
      if (dataResponse.status !== 'success') {
        $('.buttonStage2').prop('disabled', true);
        $('#abn_number-err').css('display', 'block !important');
        $('#abn_number-err').html(dataResponse.message);
        $('.business_acn_div').hide();
        $('.partnersDiv').hide();
        return;
      }

      if (!dataResponse.valid) {
        $('.progress-status.st-success').hide();
        $('.progress-status.st-fail').show();
        $('.buttonStage2').prop('disabled', true);
        $('.business_acn_div').hide();
        $('.partnersDiv').hide();
        $('#abn_number-err').css('display', 'block !important');
        $('#abn_number-err').html(dataResponse.message);
        $('#abn_approved').val(0);
        return;
      }

      const result = formatAbn(abn);
      $('#abn_number').val(result);

      $('.progress-status.st-fail').hide();
      $('.progress-status.st-success').show();
      $('#abn_approved').val(1);
      $('#business_name_text').html(dataResponse.supplier.name);
      $('#applicant_type_text').html(dataResponse.applicant_type);
      $('#business_name').val(dataResponse.supplier.name);
      $('#applicant_type').val(dataResponse.applicant_type_id);
      $('#gst_status_at').val(dataResponse.supplier.gst_status_at);

      if (typeof dataResponse.application_id !== 'undefined') {
        $('.application_id').val(dataResponse.application_id);
      }
      $('.acn_number').hide();
      switch (dataResponse.applicant_type_id) {
        case APPLICANT_TYPE_INDIVIDUAL:
          handleIndividualSoleTraderResponse()
          break
        case APPLICANT_TYPE_PARTNERSHIP:
          handlePartnershipResponse()
          break
        case APPLICANT_TYPE_COMPANY:
          handleCompanyResponse()
          break
        case APPLICANT_TYPE_TRUST:
          handleTrustResponse()
          break
      }
      handleGSTLessThan2Years(dataResponse.supplier.gst_status_at)

      function handleIndividualSoleTraderResponse() {
        $('.partnersDiv').hide();
        $('.ownershipLabel').html('<b>Does the individual own this property?</b>');
        $('#applicants_count').val(1);
        $('.business_acn_div').show();
      }

      function handlePartnershipResponse() {
        if ($('input[name=business_nature]').val() && !$('input[name=business_nature]').val().length) {
          $('#applicants_count').val('');
        }
        $('#applicants_count').val(2);
        $('.business_acn_div, .partnersDiv').show();
        $('.ownershipLabel').html('<b>Do one of the partners own this property?</b>');
        $('#no_partners')
          .closest('.form-group')
          .find('label')
          .html('<b>How many partners does the partnership have?</b>');
      }

      function handleCompanyResponse() {
        if ($('input[name=business_nature]').val() && !$('input[name=business_nature]').val().length) {
          $('#applicants_count').val('');
        }
        acnLookup($('.acn_number').val(), false);
        $('.business_acn_div, .partnersDiv').show();
        $('.ownershipLabel').html('<b>Does the company or director own this property?</b>');
        $('#no_partners')
          .closest('.form-group')
          .find('label')
          .html('<b>How many directors does the company have?</b>');
      }

      function handleTrustResponse() {
        if ($('input[name=business_nature]').val() && !$('input[name=business_nature]').val().length) {
          $('#applicants_count').val('');
        }
        acnLookup($('.acn_number').val(), false);
        $('#business_acn_name_text').html('');
        $('.acn_number, .partnersDiv').show();
        $('.business_acn_div, .progress-status-acn.st-success').hide();
        $('.ownershipLabel').html('<b>Does the company or director own this property?</b>');
        $('#no_partners')
          .closest('.form-group')
          .find('label')
          .html('<b>How many directors does the trustee company have?</b>');
      }

      function handleGSTLessThan2Years(gstRegisteredTime) {
        const isShowBankStatement = isGSTLessThan2Years(gstRegisteredTime);

        const $statementWrapper = $('.js-bank-statement-wrapper');
        if (isShowBankStatement) {
          $statementWrapper.show();
          $statementWrapper.removeClass('not_serialize collapse');
        } else {
          $statementWrapper.hide();
          $statementWrapper.addClass('not_serialize collapse');
        }
      }

      $('.buttonStage2').prop('disabled', false);
      lastSuccessfulAbnLookup = abn;

      // Don't show extra details if it's a trust (trusts need an ACN too)
      if (dataResponse.applicant_type_id !== APPLICANT_TYPE_TRUST) {
        $('.js-after-abn').show();
      }
    },
    error(response, status, error) {
      if (ajaxErrorHandler(response.status)) {
        return;
      }
      notifyBox('Error', 'Error occurred, please try again', 'error');
    }
  });
}

function isGSTLessThan2Years(gstRegisteredTime) {
  const twoYearsInMilliseconds = 2 * 365 * 24 * 60 * 60 * 1000;
  return Date.parse(gstRegisteredTime) + twoYearsInMilliseconds > Date.now();
}

/**
 * Look up an ACN and display the results
 *
 * @param acn
 * @param lengthCheck
 */
function acnLookup(acn, lengthCheck) {
  if (acn === undefined) {
    return;
  }

  acn = acn.replace(/[^0-9]/g, '');

  if (acn.length && acn === lastSuccessfulAcnLookup) {
    return;
  }
  lastSuccessfulAcnLookup = null;

  const errorField = $('#acn_number-err');
  errorField.html('');
  $('#business_acn_name_text').html('');
  $('#business_acn_name').val('');
  $('.progress-status-acn').hide();
  $('.buttonStage2').prop('disabled', true);

  if (acn.length === 0 || acn.length !== 9) {
    if (acn.length) {
      $('.partnersDiv').hide();
    }
    if (acn.length && lengthCheck) {
      errorField.css('display', 'block !important');
      errorField.html('ACN number must be 9 characters long');
    }
    return;
  }

  // Looking up ACN
  $('#acn_approved').val(0);
  $('.business_acn_div').hide();
  $('.progress-status-acn.st-success').hide();
  $('.progress-status-acn.st-fail').hide();

  const data = {
    acn,
    application_id: $('.application_id').val()
  };

  window.httpPost('/acnLookup', data, {
    success(response) {
      const dataResponse = response.data
      $('.js-display-errors').html('');
      if (dataResponse.status === 'success') {
        if (dataResponse.valid) {
          $('#business_acn_name').val(dataResponse.supplier.name);
          $('#business_acn_name_text').html(dataResponse.supplier.name);
          $('.progress-status-acn.st-success').show();
          $('#acn_approved').val(1);
          $('.business_acn_div').show();
          $('.buttonStage2').prop('disabled', false);
        } else {
          $('#acn_number-err').html('ACN verification failed');
          $('.progress-status-acn.st-fail').show();
        }
        $('.js-after-abn').show();
        $('.partnersDiv').show();
      } else {
        $('#acn_number-err').html(dataResponse.message);
        $('.progress-status-acn.st-fail').show();
      }
    },
    error(response, status, error) {
      if (ajaxErrorHandler(response.status)) {
        return;
      }
      $('#acn_number-err').html(
        'An unknown error occurred while trying to look up the ACN, please try again'
      );
      $('.progress-status-acn.st-fail').show();
    }
  });
}

function formatAbn(number) {
  number = number.replace(/\D/g, '');
  return `${number.substring(0, 2)} ${number.substring(
    2,
    5
  )} ${number.substring(5, 8)} ${number.substring(8, 11)}`;
}

/**
 * Reset hidden input field
 *
 * This is called when the user has already entered an address
 * and wants to reset or change the address on the field
 */
function addressResetField() {
  const formGroup = $(this).closest('.form-group');
  const hiddenInputs = formGroup.find('input[type="hidden"]');
  hiddenInputs.each(function () {
    $(this).val('');
  });
}

/**
 * This would reset the field to a non-error state
 */
function resetFieldError(errorSection, buttons, parentSection) {
  errorSection.removeClass('js-display-errors');
  errorSection.text('');
  buttons.prop('disabled', false);
  setTimeout(() => {
    parentSection.removeClass('has-error');
  });
}

/**
 * Business Address Validation
 *
 * This is to force user to select a value from the dropdown
 * which is being return by the google address api. If the user
 * will not select then we will force an error and disable submission
 */
function addressValidation() {
  const formGroup = $(this).closest('.form-group');
  const businessAddress = formGroup.find('#business_location').val();
  const userAddress = formGroup.find('.usrlocation').val();
  const address = businessAddress || userAddress;
  const buttons = $('.continueAfterSaving, .btn-submit');
  const addressInputValue = $(this).val();
  const message = !addressInputValue
    ? 'Required'
    : 'Please select an address from one of the address suggestions';
  const parentSection = $(this).parent();
  const errorSection = $(this).next();
  if (address) {
    resetFieldError(errorSection, buttons, parentSection);
  } else {
    errorSection.addClass('js-display-errors');
    errorSection.text(message);
    buttons.prop('disabled', true);
    setTimeout(() => {
      parentSection.addClass('has-error');
    });
  }
}

// End of Business Address Validation

// Calculating Total of Assets & Liabilities
function calculateSum($this) {
  let sumAssets = 0;
  let sumLiabilities = 0;

  // Sum up assets
  $($this.closest('.assets_liabilities_div').find('.js-assets-sum')).each(
    function () {
      if (!isNaN(this.value) && this.value.length) {
        sumAssets += parseFloat(this.value);
      }
    }
  );
  $this
    .closest('.assets_liabilities_div')
    .find('.js-assets-total')
    .text(`$${sumAssets.toFixed(2)}`);
  $this
    .closest('.assets_liabilities_div')
    .find('.js-assets-total-input')
    .val(sumAssets);

  // Sum up liabilities
  $($this.closest('.assets_liabilities_div').find('.js-liabilities-sum')).each(
    function () {
      if (!isNaN(this.value) && this.value.length) {
        sumLiabilities += parseFloat(this.value);
      } else if (this.value.length) {
        $(this).css('background-color', 'red');
      }
    }
  );

  $this
    .closest('.assets_liabilities_div')
    .find('.js-liabilities-total')
    .text(`$${sumLiabilities.toFixed(2)}`);
  $this
    .closest('.assets_liabilities_div')
    .find('.js-liabilities-total-input')
    .val(sumLiabilities);
}

export function setAutocompleteOnAddresses() {
  $('.applicant_address').each((idx, address) => {
    // Multiple Google Maps - Address
    const $this = $(address);
    const autocomplete = getGoogleMapsAutoComplete($this[0]);

    $this.on('keyup', addressResetField);
    $this.on('change', addressValidation);
    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const place = autocomplete.getPlace(); // fetch Places
      const address = place.address_components;
      let address1 = '';
      const address2 = '';
      let city = '';
      let postal_code = '';
      let state = '';
      let suburb = '';

      $.each(address, (index, addr) => {
        switch (addr.types[0]) {
          case 'street_number':
            // Address line 1
            address1 = addr.long_name;
            break;
          case 'route':
            // Address line 2
            if (address1.length) {
              address1 += ` ${addr.long_name}`;
            } else {
              address1 = addr.long_name;
            }
            break;
          case 'locality':
            suburb = addr.short_name;
            break;
          case 'administrative_area_level_2':
            city = addr.long_name;
            break;
          case 'administrative_area_level_1':
            state = addr.long_name;
            break;
          case 'postal_code':
            postal_code = addr.long_name;
            break;
          default:
            break;
        }
      });

      $this
        .closest('.user-location-box')
        .find('.usrlocation')
        .val(place.formatted_address);
      $this
        .closest('.user-location-box')
        .find('.usrlatitude')
        .val(place.geometry ? place.geometry.location.lat() : '');
      $this
        .closest('.user-location-box')
        .find('.usrlongitude')
        .val(place.geometry ? place.geometry.location.lng() : '');
      $this
        .closest('.user-location-box')
        .find('.usraddress1')
        .val(address1);
      $this
        .closest('.user-location-box')
        .find('.usraddress2')
        .val(address2);
      $this
        .closest('.user-location-box')
        .find('.usrsuburb')
        .val(suburb);
      $this
        .closest('.user-location-box')
        .find('.usrcity')
        .val(city);
      $this
        .closest('.user-location-box')
        .find('.usrstate')
        .val(state);
      $this
        .closest('.user-location-box')
        .find('.usrpostal_code')
        .val(postal_code);
      addressValidation.call($this);
      window.setTimeout(() => {
        const address = $this
          .closest('.user-location-box')
          .find('.applicant_address')
          .val();
        $this
          .closest('.user-location-box')
          .find('.js-location-as-entered')
          .val(address);
        $this
          .closest('form')
          .find('.property_address:first')
          .val(address);
      }, 100);
    });
  });
}

/**
 * It hides the form details if the radio button is set to "No" and shows the form details if the radio
 * button is set to "Yes"
 */
export const handleDisplayFormDetails = () => {
  const $radioYes = $('.input_radio_yes'),
      $radioNo = $('.input_radio_no');
  $radioYes.each(function () {
      $(this).on('click', (function () {
          const $formDetail = $(this).closest('fieldset').find('.container_detail:first');
          $formDetail.removeClass("not_serialize")
          $formDetail.show();
      }));
  });
  $radioNo.each(function () {
      $(this).on('click', (function () {
          const $formDetail = $(this).closest('fieldset').find('.container_detail:first');
          $formDetail.addClass("not_serialize")
          $formDetail.hide();
      }));
  });
}

function setAutocompleteOnPreviousAddress() {
  $('.previous_address').each(function (idx, address) {
    // Multiple Google Maps - Address
    const $this = $(address);
    const autocomplete = getGoogleMapsAutoComplete($(this)[0]);

    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const place = autocomplete.getPlace(); // fetch Places
      const address = place.address_components;
      let address1;
      let address2;
      let city;
      let postal_code;
      let state;
      let suburb;
      address1 = address2 = city = postal_code = state = suburb = '';

      $.each(address, (index, addr) => {
        if (addr.types[0] === 'locality') {
          // Address 1
          address1 = addr.long_name;
          suburb = addr.short_name;
        }
        if (addr.types[0] === 'route') {
          // Address 2
          address2 = addr.long_name;
        }
        if (addr.types[0] === 'administrative_area_level_2') {
          // City
          city = addr.long_name;
        }
        if (addr.types[0] === 'administrative_area_level_1') {
          // State
          state = addr.long_name;
        }
        if (addr.types[0] === 'postal_code') {
          // Postal Code
          postal_code = addr.long_name;
        }
      });

      $this
        .closest('.js-previous-address-container')
        .children('.prilocation')
        .val(place.formatted_address);
      $this
        .closest('.js-previous-address-container')
        .children('.priaddress1')
        .val(address1);
      $this
        .closest('.js-previous-address-container')
        .children('.priaddress2')
        .val(address2);
      $this
        .closest('.js-previous-address-container')
        .find('.prisuburb')
        .val(suburb);
      $this
        .closest('.js-previous-address-container')
        .find('.pricity')
        .val(city);
      $this
        .closest('.js-previous-address-container')
        .find('.pristate')
        .val(state);
      $this
        .closest('.js-previous-address-container')
        .find('.pripostal_code')
        .val(postal_code);
    });
  });
}

/**
 * It initializes the dropzone library for each dropzone element in the page
 */
export function dropzonesUploadFiles() {
  let messageError = 'Error occurred, The file not saved';
  $('.dropzones').each(function () {
    const url = $(this).attr('action');
    const $form = $(this).closest('fieldset.form-group');
    let documentType = parseInt($form.find('.document_type').val());
    let params = {
      timeout: 0,
      maxFilesize: 10,
      acceptedFiles: '.pdf, .jpg, .jpeg, .png, .csv',
      url: url,
      uploadMultiple: true,
      params: {
        document_type: documentType,
      },
      headers: {
        'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content'),
      },
      sending: () => {
        $('.js-loading').show();
        $('.js-loading').hide();
      },
      sending: (_, xhr, __) => {
        xhr.ontimeout = function () {
          notifyBox(
            'Error',
            'Timeout, please check your internet connection',
            'error',
          );
        };
      },
      success: (_, response) => {
        if (response.status === 'success') {
          const data = response.data;
          notifyBox('Uploaded file sucess', '', 'success');
          const $downloadLink = $form.find('.js-download-files');
          const links = data
            .filter((file) => file.document_type === documentType)
            .map((file) => file.url);
          $downloadLink.val(JSON.stringify(links));
          $form.find('.file_names').val(JSON.stringify(links));
          $form.find('.js-download-filecount').text(links.length);
          $form.find('.js-download-link').removeClass('hidden');
          $form.find('.js-display-errors').html('');
        } else {
          notifyBox('Error', messageError, 'error');
        }
        $('.js-loading').hide();
      },
      error: (file, message, request) => {
        const requestStatus = request && request.status;

        if (requestStatus === 442) {
          displayErrorMessage($(this), message.errors);
        } else {
          notifyBox('Error', message, 'error');
        }

        $('.js-loading').hide();
        if ($(this).hasClass('payslip-file')) {
          $(file.previewElement).remove();
        }
      },
    };
    $(this).dropzone(params);
  });
}

/**
 * It takes a JSON string of file names, parses it into an array, and then passes that array to a
 * function that downloads each file in the array.
 */
export const downloadMultipleFiles = () => {
  $('.js-download-link').on('click', function (e) {
      e.preventDefault();
      const jsonFiles = $(this).find('.files_name-input').val();
      window.downloadLinks(JSON.parse(jsonFiles));
  });
}

 /**
   * Responsible for showing or hiding the
   * application pages
   *
   * @return void
   */
 export function sidebarNavDisplay(e, currentElement, navListItems, allWells) {
  e.preventDefault();

  const $stage = currentElement.attr('href').replace('#', '');
  const $target = $(`div[data-stage="${$stage}"]`);

  if (!currentElement.hasClass('disabled')) {
    navListItems.removeClass('btn-active').addClass('btn-default');
    currentElement.addClass('btn-active');
    allWells.hide();
    $target.show();
    $target.find('input:eq(0)').focus();
    window.location.hash = $stage;
  }
}

window.sfDocumentReady([
  'route-application-edit',
], () => {
  handleAddEventSmartlook('New Application', APPLICATION_COMMERCIAL);
});

window.sfDocumentReady([
  'route-application-residential-edit'
], () => {
  handleAddEventSmartlook('New Application', APPLICATION_RESIDENTIAL);
});

window.sfDocumentReady([
  'route-application-car-edit'
], () => {
  handleAddEventSmartlook('New Application', APPLICATION_CAR);
});

// Document
window.sfDocumentReady([
  'route-application-edit',
  'route-application-residential-edit',
  'route-supplier-accreditation-edit',
  'route-application-car-edit',
  'route-application-stock-finance-edit'
], () => {
  $('.showDiv').click(() => {
    $('.hideForm').fadeToggle();
  });

  $('.indinavs').each(e => {
    $('#individualEdit').after(
      '<div class="reviewApplication " id="individualEdit"><h4>Individual 1  <span class="resStage3"></span></h4><a href="#individual1">Edit </a></div>'
    );
  });

  $('[name="finance_type"]').change(function (e) {
    if ($(this).val() == 2) {
      $('.assets_liabilities_div').show();
    } else {
      $('.assets_liabilities_div').hide();
    }
  });

  $('body').on('click', '.reviewEditLink', function (e) {
    $(`a[type="button"][href="${$(this).attr('href')}"]`).click();
    $('html, body').animate(
      {
        scrollTop: 0
      },
      100
    );
  });

  // Personalise 'joint assets' label when first name updates
  $('input[name=first_name]').change(function () {
    const name = $(this).val();
    $(this)
      .closest('form')
      .find('.js-individual-1-name')
      .each(function () {
        $(this).html(name);
      });
  });
  $('input[name=first_name]').trigger('change');

  // =============================================
  // BEGIN::SIDE BAR NAVIGATION SCRIPT
  // =============================================
  /**
   * Triggers equifax lookups for acn and abn
   *
   * @return void
   */
  function equifaxLookups() {
    const abnNumberField = $('#abn_number');
    // Trigger ABN number lookups on data input
    abnNumberField.on('input', () => {
      const isBusinessPage = window.location.hash === '#business';
      const businessNameEmpty = !$('#business_name_text').val().length;
      if (isBusinessPage && businessNameEmpty) {
        const acnHasValue = abnNumberField.length;
        const disableElement = !acnHasValue;
        $('.buttonStage2').prop('disabled', disableElement);
        abnLookup(abnNumberField.val(), true);
      }
    });
    // Trigger ACN number lookups
    const acnNumberField = $('#acn_number');
    acnNumberField.on('input', () => {
      const isBusinessPage = window.location.hash === '#business';
      const businessNameEmpty = !$('#business_name_text').val().length;
      if (isBusinessPage && businessNameEmpty) {
        acnLookup(acnNumberField.val(), false);
      }
    });
  }

  const navListItems = $('.setup-panel div a');
  const allWells = $('.setup-content');
  const allNextBtn = $('.nextBtn');
  const allBackBtn = $('.backBtn');

  equifaxLookups();

  // initially hide all the pages
  allWells.hide();

  // Side bar links trigger
  // run the sidebarNavDisplay function
  navListItems.click(function (e) {
    sidebarNavDisplay(e, $(this), navListItems, allWells);
  });

  // Back Button To & From
  allBackBtn.click(function () {
    const curStep = $(this).closest('.setup-content');
    const curStepBtn = curStep.attr('data-stage');
    const nextStepWizard = $(`.setup-panel div a[href="#${curStepBtn}"]`)
      .parent()
      .prev()
      .children('a');

    const isValid = true;
    if (isValid) {
      nextStepWizard.trigger('click');
    }
  });

  // Next Button To & From
  allNextBtn.click(function () {
    // scroll to top of the Current Page on next
    $(window).scrollTop(0);

    const curStep = $(this).closest('.setup-content');
    const curStepBtn = curStep.attr('data-stage');
    const curInputs = curStep.find('input[type="text"],input[type="url"]');
    let isValid = true;
    const nextStepWizard = $(`.setup-panel div a[href="#${curStepBtn}"]`)
      .parent()
      .next()
      .children('a');

    // clean up the error classes
    $('.form-group').removeClass('has-error');

    // checks if inputs are valid
    // if not valid add has-error class to element
    // adds disabled attribute to all elements
    for (let i = 0; i < curInputs.length; i++) {
      if (!curInputs[i].validity.valid) {
        isValid = false;
        $(curInputs[i])
          .closest('.form-group')
          .addClass('has-error');
        nextStepWizard.attr('disabled').trigger('click');
      }
    }

    // If all input elements are valid, remove all attribute disabled
    if (isValid) {
      nextStepWizard.removeAttr('disabled').trigger('click');
    }
  }); // ===== END::SIDE BAR NAVIGATION =====

  /**
   * Handles adding dynamic individual forms
   * Currently this script handles only dynamic forms maximum of 6
   * max forms is somehow fixed if you refresh the page
   *
   */
  function appendIndividuals() {
    // clear out the applicants count error
    $('#applicants_count-err').html('');

    const applicantsCount = parseInt($('#applicants_count').val());
    const applicantsCountOld = parseInt($('#applicants_count_old').val());
    let counter = applicantsCountOld + 2;

    // determine the required number of applicants to generate
    const requiredNumber =
      applicantsCount > applicantsCountOld
        ? applicantsCount - applicantsCountOld
        : 0;

    if (applicantsCount > applicantsCountOld) {
      for (let i = 1; i <= requiredNumber; i++) {
        const nav = applicantsCountOld + i;

        // create navigation element (sidebar link)
        const navdata =
          '' +
          `<div class="navIndividual_${nav} navIndividual indinavs stepwizard-step" type="applicant">` +
          `<a href="#individual${nav}" type="button" class="indinavs btn btn-default btn-circle sf-p-0">` +
          `${counter + 1}` +
          '</a>' +
          `<h6>Individual ${nav}</h6>` +
          '</div>';

        // attach the new sidebar link for each new individual
        $('.stage-navigation .product').before(navdata);

        // attach individual form edit links on the review page
        // placed before the product link
        const position = nav + 6;

        // ensures that the individual elements in review page
        // will not generate duplicates, by default the element has a
        // complete this step error message
        if ($(`#individualEdit${position}`).length <= 0) {
          $('#productReview').before(
            `<div class="reviewApplication" id="individualEdit${position}">` +
            // begin title
            `<div class="sf-flex">` +
            '<div class="review-title">' +
            '<h4>' +
            `Individual ${nav} <span class="resStage${position}"></span>` +
            '</h4>' +
            '</div>' + // end title title

            // begin status
            '<div class="review-status">' +
            '<div class="sf-flex sf-justify-end">' +
            '<div class="review-text">' +
            `<span class="resStage${position} status sf-text-lg sf-text-grey"></span>` +
            '</div>' +

            '<div class="review-image">' +
            `<span class="resStage${position} image"></span>` +
            '</div>' +
            '</div>' +
            '</div>' + // end status
            '</div>' +

            // BEGIN::Description
            '<div class="sf-flex">' +
            '<div class="sf-w-full">' +
            `<p id="resStage${position}" class="resStage${position}"></p>` +
            `<a href="#individual${nav}" class="reviewEditLink">` +
            'Complete this step' +
            '</a>' +
            '</div>' +
            '</div>' + // END::Description
            '</div>'
          );
        }

        // clone the individual form template
        const individualFormTemplate = $('#new-applicant-clone');
        if ($('input[name="finance_type"]:checked').val() === 2) {
          // Adding new applicant to chattel application
          individualFormTemplate.find('.residency_div').show();
          individualFormTemplate.find('.salutation_div').show();
          if ($('input[name=has_joint_assets]').val()) {
            individualFormTemplate.find('.assets_liabilities_div').hide();
          } else {
            individualFormTemplate.find('.assets_liabilities_div').show();
          }
        } else {
          // Adding new applicant to rental application
          individualFormTemplate.find('.residency_div').hide();
          individualFormTemplate.find('.salutation_div').hide();
          individualFormTemplate.find('.assets_liabilities_div').hide();
        }

        // attach data attribute individual index to the form element
        individualFormTemplate
          .find('.individual_id')
          .attr('data-individual-index', i);

        // This appends the form of individuals
        // there is a bug here, if you generate multiple forms and revert
        // one form then regenerate multiple, those forms will appear all together
        // this happens only on a new application
        const stepData =
          '<div class="setup-content" ' +
          `id="step-${applicantsCountOld + i + 6}" ` +
          `data-stage="individual${applicantsCountOld + i}">` +
          `${individualFormTemplate
            .html()
            .replace(/%%index%%/g, applicantsCountOld + i + 6)}` +
          '</div>';

        $('.step-content-block').append(stepData);

        // Set autocomplete on new address fields
        setAutocompleteOnAddresses();
        handleAddAnotherProperty();

        var $individual = $(`[data-stage="individual${nav}"`);
        // set the correct id for the radio elements
        $individual.find('[type="radio"]').each(function () {
          $(this).attr('id', $(this).attr('id') + i + 6);
          $(this)
            .siblings('label')
            .attr('for', $(this).attr('id'));
        });

        // Reset any inadvertently pre-filled A&L data
        $individual.find('.js-assets-sum').val('');
        $individual.find('.js-liabilities-sum').val('');
        $individual.find('.js-living-history').val('');

        if ($('[name="finance_type"]:checked').val() == 2) {
          $('.assets_liabilities_div').show();
        }
        counter++;
      }

      // Date of Birth
      bindDobInputs();

      setTimeout(() => {
        $('.stage-navigation .stage-nav-block-after').each(function (e) {
          $(this)
            .find('a')
            .html(counter + 1);
          counter++;
        });

        const navListItems = $('.setup-panel div a');
        const allWells = $('.setup-content');
        const allNextBtn = $('.nextBtn');
        allWells.hide();
        $('div[data-stage="business"]').show();

        // Side bar links trigger
        // run the sidebarNavDisplay function
        // hides and shows individuals for newly appended pages
        navListItems.on('click', function (e) {
          sidebarNavDisplay(e, $(this), navListItems, allWells);
        });

        $('#applicants_count_old').val(applicantsCount);

        // Floating placeholder labels for new form elements
        $('.ffl-wrapper').floatingFormLabels({
          formElements: 'input, textarea, select'
        });
      }, 200);
    }
  }

  // Disable enter key on application form stages
  // TODO ideally, enter would function as the next button
  $(document).on('keyup keypress', 'form.form-stage', e => {
    const keyCode = e.keyCode || e.which;
    if (keyCode === 13) {
      e.preventDefault();
      return false;
    }
  });

  function isShowBankStatement() {
    const systemCost = $('#system_cost').val();
    const formatToNumber = Number(systemCost.replace(/[^0-9\.]+/g, ""));
    const $statementWrapper = $('.js-bank-statement-wrapper');
    if (Number(formatToNumber) > MINIMUM_BANK_STATEMENT) {
      $statementWrapper.show();
      $statementWrapper.removeClass('not_serialize collapse');
    } else {
      $statementWrapper.hide();
      $statementWrapper.addClass('not_serialize collapse');
    }
  }

  function handleError(response, $context, $navItem) {
    if (response.responseJSON && response.responseJSON.errors) {
      const errors = response.responseJSON.errors;
      displayErrorMessage($context, errors, response.status)
    } else {
      notifyBox('Error', 'An unexpected error occurred. Please try again', 'error');
    }
    $('html, body').animate(
      {
        scrollTop: 0
      },
      100
    );
    if (ajaxErrorHandler(response.status)) {
      return;
    }
    $navItem.removeClass('step-complete');
  }

  // Step 1 - Basic Information
  $('.buttonStage1').click(function () {
    const form = $('#stage1');
    const dataForm = form.serializeArray();
    const rules = BASIC_RULES

    const applicationType = Number($('#application_type').val());

    if (![APPLICATION_RESIDENTIAL, APPLICATION_CAR].includes(applicationType)) {
      rules['system_cost_duration'] = generateRequiredRule()
    }

    if (applicationType === APPLICATION_CAR) {
      rules['asset_condition_status'] = generateRequiredRule()
      var assetConditionValue = parseInt($("input[name='asset_condition_status']:checked").val());
      console.log('assetConditionValue', assetConditionValue);
      if (assetConditionValue === ASSET_CONDITION_USED) {
        rules['type_sale'] = generateRequiredRule()
        rules['year_model'] = generateRequiredRule()
      } else {
        rules['type_sale'] = {}
        rules['year_model'] = {}
      }
    } else if (applicationType === APPLICATION_STOCK_FINANCE) {
      rules['term'] = {}
    }

    const isValid = validate(dataForm, form, rules);
    if (!isValid) return false

    if (isDirty(form) && !confirmPrivacyNullification()) {
      form.trigger('reset');
      return;
    }

    const curStep = $(this).closest('.setup-content');
    const curStepBtn = curStep.attr('data-stage');
    const navItem = $(`.setup-panel div a[href="#${curStepBtn}"]`).parent();
    const nextStepWizard = navItem.next().children('a');
    const continueAfterSaving = $(this).hasClass('continueAfterSaving');

    saveApplication(dataForm, {
      success(data) {
        data = data.data
        $('.js-display-errors').html('');
        $('.application_id').val(data.id);
        $('.finance_type').val(data.finance_type);
        $('.applicants_count').val(data.applicants_count);
        isShowBankStatement();
        if (data.finance_type == 1) {
          // Finance type set to rental
          $('.residency_div').hide();
          $('.salutation_div').hide();
        } else {
          // Finance type set to chattel
          $('.residency_div').show();
          $('.salutation_div').show();
          $('.js-joint-assets').show();
        }
        if ([APPLICATION_RESIDENTIAL, APPLICATION_CAR].includes(parseInt(data.application_type))) {
          handleAddOneStage(data.applicants_count, 'individual', false)
          handleAddOneStage(data.applicants_count, 'income', true)
          $('#income-content-2').find('input.individual_id').val($('#individual-content-2 input.applicant_id').val());
        }

        // Return home or go to next stage
        if (continueAfterSaving) {
          handleAddEventSmartlook('Step basic', Number($('#application_type').val()), startTimeCreateCommercialStep);

          navItem.addClass('step-complete');
          nextStepWizard.trigger('click');
          $('html, body').animate(
            {
              scrollTop: 0
            },
            100
          );
        } else {
          // redirect to list of applications on save & exit
          window.location.href = '/';
        }
      },
      error(response, status, error) {
        handleError(response, form, navItem)
      }
    });
  });

  // Step 2 - Business Information
  $('.buttonStage2').click(async function () {
    await stage2($(this));
  });

  // Step 3 - Individual Information Saving
  $(document).on('click', '.applicantSave', async function () {
    await stage3($(this));
  });

  // Step 4 - Product Information
  $('.buttonStage4').click(async () => {
    await stage4();
  });

  /**
   * Saving Business Information
   * This is used both by save next button
   *
   * @return {Promise<void>}
   */
  async function stage2($this) {
    const form = $('#stage2');
    const dataForm = form.serializeArray();
    const rules = BUSINESS_RULES

    if (Number($('#applicant_type').val()) === APPLICANT_TYPE_TRUST) {
      rules['acn_number'] = generateRequiredRule()
      rules['business_acn_name'] = generateRequiredRule()
    }

    if (parseInt($("#application_type").val()) === APPLICATION_COMMERCIAL && isGSTLessThan2Years($('#gst_status_at').val()) ) {
      rules['application_documents[1][status]'] = generateRequiredRule()
    }

    const isValid = validate(dataForm, form, rules);
    if (!isValid) return false

    const abnNumber = $('#abn_number')
      .val()
      .replace(/\D/g, '');

    if (!abnNumber.length) {
      notifyBox('Please correct the following:', 'ABN is required', 'error');
      return;
    }

    if (abnNumber.length !== 11) {
      notifyBox(
        'Please correct the following:',
        'ABN must be 11 digits',
        'error'
      );
      return;
    }

    const $applicantsCount = $('#applicants_count');
    const $applicantsCountOld = $('#applicants_count_old');

    const applicantsCount = parseInt($applicantsCount.val());
    const applicantsCountOld = parseInt($applicantsCountOld.val());
    const currentApplicant = applicantsCountOld - applicantsCount;

    if (applicantsCount > 1) {
      $('.js-joint-assets').show();
      $('.individualRemove').show();
      const $reviewItemCloned = $(`#resStage_individual1.reviewApplication`).clone(true)
      $reviewItemCloned.attr('id', `resStage_individual2`);
      $reviewItemCloned.find('.review-title h4').html(`individual 2`);
      $reviewItemCloned.find('a.edit-stage-link').attr('href', `#individual2`)
      $reviewItemCloned.insertAfter(`#resStage_individual1.reviewApplication`)
    } else {
      $('.js-joint-assets').hide();
      $('.individualRemove').hide();
      $(`#resStage_individual2.reviewApplication`).remove()
    }

    if (applicantsCount === 0) {
      $applicantsCount.val(applicantsCountOld);
      notifyBox(
        'Validation error',
        'Application must have at least 1 director/partner',
        'error'
      );
      return;
    }

    if (isDirty(form) && !confirmPrivacyNullification()) {
      form.trigger('reset');
      return;
    }

    const curStep = $this.closest('.setup-content');
    const curStepBtn = curStep.attr('data-stage');
    const navItem = $(`.setup-panel div a[href="#${curStepBtn}"]`).parent();
    const nextStepWizard = navItem.next().children('a');
    const continueAfterSaving = $this.hasClass('continueAfterSaving');

    saveApplication(dataForm, {
      success(data) {
        data = data.data
        $('.js-display-errors').html('');
        // handles removal of unused individuals (depends on number of directors)
        if (currentApplicant > 0) {
          let nav = applicantsCountOld;
          for (let i = 1; i <= currentApplicant; i++) {
            const productSrNo = parseInt(3 + applicantsCount);
            $(`.navIndividual_${nav}`).remove();
            $('.product a').html(parseInt(productSrNo));
            $('.consent a').html(parseInt(productSrNo + 1));
            $('.review a').html(parseInt(productSrNo + 2));
            $('.reviewIndividual:last').remove();

            if (applicantsCount > 1) {
              $('.js-joint-assets').hide();
              $('.individualRemove').show();
            } else {
              $('.js-joint-assets').show();
              $('.individualRemove').hide();
            }
            nav--;
          }

          // set element applicants count old with applicants count value
          $applicantsCountOld.val(applicantsCount);
          handleReStepWizard()
        }

        $('.application_id').val(data.id);

        // Return home or go to next stage
        if (continueAfterSaving) {
          handleAddEventSmartlook('Step business', APPLICATION_COMMERCIAL, startTimeCreateCommercialStep);
          navItem.addClass('step-complete');

          // Handles appending new individual forms
          // depends also on number of directors from business form
          appendIndividuals();

          setTimeout(() => {
            nextStepWizard.trigger('click');
          }, 200);

          $('html, body').animate(
            {
              scrollTop: 0
            },
            100
          );
        } else {
          window.location.href = '/';
        }
      },

      error(response, status, error) {
        handleError(response, form, navItem)
      }
    });
  }

  /**
   * Saving Individual Information
   *
   * This is used both by save next button and side bar nav
   * @param $this
   * @return {Promise<void>}
   */
  async function stage3($this) {
    if ($('.application_id').val()) {
      const form = $this.closest('form');
      const data = form.serializeArray();
      const rules = handleIndividualCommercialRules(data, form)
      const isValid = validate(data, form, rules);
      if (!isValid) return false;

      if (isDirty(form) && !confirmPrivacyNullification()) {
        form.trigger('reset');
        return;
      }

      const curStep = $this.closest('.setup-content');
      const curStepBtn = curStep.attr('data-stage');
      const navItem = $(`.setup-panel div a[href="#${curStepBtn}"]`).parent();
      const nextStepWizard = navItem.next().children('a');

      data.push(
        {
          name: "phone[0][work_phone]",
          value: form.find('.work_phone').val().replace(/\s/g, '')
        },
        {
          name: "phone[0][work_phone_type]",
          value: 1
        });
      data.push({ name: 'application_id', value: $('.application_id').val() });

      saveApplication(data, {
        success(data) {
          data = data.data
          $('.js-display-errors').html('');
          if (data.status === 'success') {
            navItem.addClass('step-complete');
            $this
              .closest('form')
              .find('.applicant_id')
              .val(data.applicant_id);
            const propertyStatus = data.data.propertyStatus;
            $this.closest('form').find('.individual_property_id').each(function (index) {
              if (propertyStatus[index]) {
                $(this).val(propertyStatus[index].id)
                if (propertyStatus[index].isDeleted) {
                  $(this).closest('.individual-property-container').remove();
                }
              }
            })

            if ($this.hasClass('continueAfterSaving')) {
              handleAddEventSmartlook('Step individual', APPLICATION_COMMERCIAL, startTimeCreateCommercialStep);
              nextStepWizard.trigger('click');
            } else {
              // redirect to listing on save & exit
              window.location.href = '/';
            }
            $('html, body').animate(
              {
                scrollTop: 0
              },
              100
            );

            return;
          }
        },

        error(response, status, error) {
          handleError(response, form, navItem)
        }
      });
    } else {
      notifyBox(
        'Unable to save',
        'Please fill basic and business information first',
        'error'
      );
    }
  }

  /**
   * Saving Product Information
   *
   * This is used both by save next button and side bar nav
   * @param nextStep
   * @return {Promise<void>}
   */
  async function stage4(nextStep = true) {
    const $form = $('#stage4');

    // Normalize tagify selected products
    // Solar panels & inverters
    $('#stage4 .js-product-model-input').each(function () {
      $(this).val(normalizeTaggedValue($(this).val()));
    });

    const dataForm = $form.find("input, select").not(".not_serialize input, .not_serialize input[type=radio], .not_serialize select").serializeArray();
    const rules = handleProductRules(dataForm, $form)
    const isValid = validate(dataForm, $form, rules);
    if (!isValid) return false;

    const headerErrorMessage = $('.js-product-list-errors');
    const buttonStage4 = $('.buttonStage4');
    if (isDirty($form) && !confirmPrivacyNullification()) {
      $form.trigger('reset');
      return;
    }

    const curStep = buttonStage4.closest('.setup-content');
    const curStepBtn = curStep.attr('data-stage');
    const navItem = $(`.setup-panel div a[href="#${curStepBtn}"]`).parent();
    const nextStepWizard = navItem.next().children('a');
    const continueAfterSaving = buttonStage4.hasClass('continueAfterSaving');

    await saveApplication(dataForm, {
      async success(data) {
        $('.js-display-errors').html('');
        $('.error_div-err').html('');
        headerErrorMessage.hide();

        // go to next step
        if (nextStep) {
          if (continueAfterSaving) {
            handleAddEventSmartlook('Step product', APPLICATION_COMMERCIAL, startTimeCreateCommercialStep);
            navItem.addClass('step-complete');
            nextStepWizard.trigger('click');
            $('html, body').animate(
              {
                scrollTop: 0
              },
              100
            );
          } else {
            // redirect to listing on save & exit
            window.location.href = '/';
          }
        }

        $('.error_div-err').hide();

        // runs the review page validation upon completion of product save
        // only if the next page is the review page
        await runReviewPageValidation();
      },

      error(response, status, error) {
        handleError(response, $form, navItem)
        headerErrorMessage.each(function () {
          if ($(this).find('.js-display-errors').html())
            $(this).show()
          else
            $(this).hide()
        })
      }
    });
  }

  $(document).on('change', '.productTypeOptions', function () {
    if (parseInt($(this).val()) === 2) {
      $(this)
        .closest('tr')
        .find('.js-product-model-input')
        .attr('placeholder', 'E.g. 330W Phono');
    } else if (parseInt($(this).val()) === 1) {
      $(this)
        .closest('tr')
        .find('.js-product-model-input')
        .attr('placeholder', 'E.g. 10kW Fronius');
    } else {
      $(this)
        .closest('tr')
        .find('.js-product-model-input')
        .attr('placeholder', 'Model');
    }
  });

  if ($('#applicants_count_old').val() > $('#applicants_count').val()) {
    $('#applicants_count').val($('#applicants_count_old').val());
  }

  // Show manual postal address field if needed
  $(document).on('input', '.postal_address', function () {
    if (parseInt($(this).val()) === POSTAL_ADDRESS_MANUAL) {
      $(this)
        .closest('form')
        .find('.js-postal-manual-address-field')
        .show();
    } else {
      $(this)
        .closest('form')
        .find('.js-postal-manual-address-field')
        .hide();
    }
  });

  // Trigger manual postal address field on page load if needed
  $('.postal_address').each(function () {
    if (parseInt($(this).val()) === POSTAL_ADDRESS_MANUAL && $(this).prop('checked')) {
      $(this).trigger('input');
    }
  });

  // Require a minimum of 2 individuals for partnerships
  $(document).on('blur', '#applicants_count', function () {
    const errorField = $('#applicants_count-err');
    errorField.hide();
    if (
      parseInt($('#applicant_type').val()) === APPLICANT_TYPE_PARTNERSHIP &&
      parseInt($(this).val()) === 1
    ) {
      errorField
        .show()
        .html('A minimum of 2 individuals are required for a partnership');
      $(this).val(2);
    }
  });

  let extraLivingHistoryFieldCounter = 1000;

  // Living history input changes
  $(document).on('blur change keyup keydown', '.js-living-history', function () {
    let totalYears = 0;

    $(this)
      .closest('.rentDiv')
      .find('.js-previous-address-container')
      .each(function () {
        if (totalYears >= 3) {
          $(this).remove();
          return;
        }

        const years = Number(
          $(this)
            .find('.js-living-history-years')
            .val()
        );
        const months = Number(
          $(this)
            .find('.js-living-history-months')
            .val()
        );
        totalYears += years + months / 12;
        setAutocompleteOnPreviousAddress();
      });

    if (totalYears < 3) {
      // Don't add more fields if the last previous address data is not yet
      // filled in
      if (
        Number(
          $(this)
            .closest('.rentDiv')
            .find('.js-living-history-years:last')
            .val()
        ) +
        Number(
          $(this)
            .closest('.rentDiv')
            .find('.js-living-history-months:last')
            .val()
        ) ===
        0
      ) {
        return;
      }

      const count = extraLivingHistoryFieldCounter++;
      const index = Date.now();
      let ownership = '';
      if ([APPLICATION_RESIDENTIAL, APPLICATION_CAR].includes(parseInt($('#application_type').val()))) {
        ownership = `
        <div class="form-group house_ownership_div">
            <label class="control-label sf-mt-3"><strong>Did the individual own the property?</strong></label>
            <div class="row">
                <div class="col-md-3 col-sm-6">
                    <input type="radio" class="form-control alignradio house_ownership"
                        name="previous_address[${count}][property_type]" id="outrightEdit-${index}" value="${OWNERSHIP_OWNS_OUTRIGHT}">
                    <label class="alignlabel" for="outrightEdit-${index}">Owns outright</label>
                </div>
                <div class="col-md-3 col-sm-6">
                    <input type="radio" class="form-control alignradio house_ownership"
                        name="previous_address[${count}][property_type]" id="mortgageEdit-${index}" value="${OWNERSHIP_MORTGAGE}">
                    <label class="alignlabel" for="mortgageEdit-${index}">Owns on a mortgage</label>
                </div>
                <div class="col-md-3 col-sm-6">
                  <input type="radio" class="form-control alignradio house_ownership"
                        name="previous_address[${count}][property_type]" id="rentEdit-${index}" value="${OWNERSHIP_RENTS}">
                  <label class="alignlabel" for="rentEdit-${index}">Rents</label>
                </div>
                <div class="col-md-3 col-sm-6">
                  <input type="radio" class="form-control alignradio house_ownership"
                      name="previous_address[${count}][property_type]" id="relativesEdit-${index}" value="${OWNERSHIP_LIVES_WITH_RELATIVES}">
                  <label class="alignlabel" for="relativesEdit-${index}">Lives with relatives</label>
                </div>
            </div>
            <div class="error-span js-display-errors previous_address_${count}_property_type-err"></div>
        </div>`
      }

      const data =
        `${'' +
        '<div class="col-md-12 form-group js-previous-address-container">' +
        '<div class="input-group"> ' +
        '<label class="control-label sf-mt-4 sf-mb-4 sf-w-full"><b>What was the individual\'s previous address?</b> </label> ' +
        '<input type="text" class="form-control previous_address" name="previous_address['}${count}][previous_location]" type="text" placeholder="Start typing..." value="" /> ` +
        `<div class="error-span js-display-errors previous_address_${count}_previous_location-err" ></div> ` +
        '</div> ' + ownership +
        `<input name="previous_address[${count}][id]" class="" value="" type="hidden"> ` +
        `<input name="previous_address[${count}][address1]" class="priaddress1" type="hidden"> ` +
        `<input name="previous_address[${count}][address2]" class="priaddress2" type="hidden"> ` +
        `<input name="previous_address[${count}][suburb]" class="prisuburb" type="hidden"> ` +
        `<input name="previous_address[${count}][city]" class="pricity" type="hidden"> ` +
        `<input name="previous_address[${count}][state]" class="pristate" type="hidden"> ` +
        `<input name="previous_address[${count}][postal_code]" name="postal_code" class="pripostal_code" type="hidden"> ` +
        '<div class="year_month_div"> ' +
        '<label class="control-label sf-mt-2 sf-mb-4 sf-w-full"><b>How long has the individual been living at this address?</b> </label> ' +
        '<div class="flow-root"></div> ' +
        '<div class="col-md-2 col-sm-3 sf-pl-0"> ' +
        `<input type="text" class="form-control js-living-history js-living-history-years js-positive-integers-only" name="previous_address[${count}][years]" id="living_from_years" placeholder="Years" value="0" /> ` +
        `<div class="error-span js-display-errors previous_address_${count}_years-err"></div> ` +
        '</div> ' +
        '<div class="col-md-2 col-sm-3 sf-mt-2"> ' +
        '<label class="control-label">years and</label> ' +
        '</div> ' +
        '<div class="col-md-2 col-sm-3 sf-pr-0"> ' +
        `<input type="text" inputmode="numeric" class="form-control js-living-history js-living-history-months js-positive-or-zero-integers-only" name="previous_address[${count}][months]" id="months[${count}]" placeholder="Months" value="0" /> ` +
        `<div class="error-span js-display-errors previous_address_${count}_months-err"></div> ` +
        '</div> ' +
        '<div class="col-md-2 col-sm-3 sf-mt-2"> ' +
        '<label class="control-label">months</label> ' +
        '</div> ' +
        '</div> ' +
        '</div>';

      $(this)
        .closest('.rentDiv')
        .append(data);
    }
  });

  // Trigger living history input changes
  $('.js-living-history').trigger('change');

  // Set valid status for ABN numbers if applicable (why is this done in JS at
  // init?)
  if (parseInt($('#abn_approved').val()) === 1) {
    // Edit mode - set success for Valid ABN Number
    $('.progress-status.st-success').show();
    $('#business_name').prop('readonly', true);
  }

  // Set valid status for ACN numbers if applicable (why is this done in JS at
  // init?)
  if (parseInt($('#acn_approved').val()) === 1) {
    // Edit mode -
    $('.progress-status-acn.st-success').show();
  }

  // Hide number of applicants field for individual applicant types
  if (parseInt($('#applicant_type').val()) === APPLICANT_TYPE_INDIVIDUAL) {
    $('.partnersDiv').hide();
  }

  /**
   * Checks if there are any incomplete stages
   *
   * @returns {boolean}
   */
  function stagesIncomplete() {
    let incomplete = false;
    const pageStatus = $('.reviewApplication').find('span.status');

    pageStatus.each(function () {
      const element = $(this);
      const elementClass = element.attr('class');
      if (elementClass.indexOf('resStage5') < 0) {
        const elementText = element.html();
        if (elementText.toLowerCase() === 'incomplete') {
          incomplete = true;
        }
      }
    });

    return incomplete;
  }

  /**
   * Get all data separated by comma
   *
   * @param selector jquery selector
   * @returns {string}
   */
  function getAllData(selector) {
    const elements = $('.form-stage').find(selector);
    let data = '';
    let filteredElements = [];

    // if no elements return blank
    if (!elements.length) {
      return data;
    }

    // remove invalid elements (blank values etc)
    filteredElements = elements.filter((index, element) => {
      if ($(element).val()) {
        return element;
      }
    });

    // add commas or "and" on last element, cleans commans on last element
    filteredElements.map((index, element) => {
      if (
        filteredElements.length >= 2 &&
        index === filteredElements.length - 1
      ) {
        data = data.replace(/(^\,+|\,+$)/gm, '');
        data += ` and ${$(element).val()}`;
      } else {
        data += ` ${$(element).val()},`;
      }
    });

    // trims spaces and commas
    return data
      .replace(/(^\,+|\,+$)/gm, '')
      .trim()
      .replace(/(^\,+|\,+$)/gm, '');
  }

  // Privacy consent type: show/hide instructions and submit buttons based on
  // selection
  $(document).on('change', 'input[name=privacy_consent_type]', () => {
    const consent = Number($('input[name=privacy_consent_type]:checked').val());
    const textDigital = $('.js-privacy-text-digital');
    const textManual = $('.js-privacy-text-manual');
    const textDefaultManual = $('.js-privacy-text-default-manual');
    const textDefaultDigital = $('.js-privacy-text-default-digital');
    const submitApplication = $('.submitApplication');
    const digitalConsent = 1;
    const manualConsent = 2;
    const esignatureConsent = 3;
    const phones = getAllData('.work_phone');
    const emails = getAllData('[name="contact_email"]');

    switch (consent) {
      case digitalConsent:
      case esignatureConsent:
        // text shown when digital has been chosen yet saved
        textManual.hide();
        textDigital.show();
        textDefaultManual.hide();

        if (textDigital.html().trim().length <= 0) {
          $('#privacy-text-digital-phones').html(phones);
          $('#privacy-text-digital-emails').html(emails);
          textDefaultManual.hide();
          // text shown when digital has already been chosen and saved
          textDefaultDigital.show();
        }

        // if any of the stages is incomplete submit button will remain disabled
        if (!stagesIncomplete()) {
          submitApplication.prop('disabled', false);
        }
        break;

      case manualConsent:
        textDigital.hide();
        textManual.show();

        if (!textManual.html().trim().length) {
          textDefaultDigital.hide();
          textDefaultManual.show();
        }

        // if any of the stages is incomplete submit button will remain disabled
        if (!stagesIncomplete()) {
          submitApplication.prop('disabled', false);
        }
        break;

      default:
        // Submit application button status
        submitApplication.prop('disabled', true);
    }
  });

  $('input[name=privacy_consent_type]').trigger('change');

  // Joint assets toggle
  $(document).on('change', '#has_joint_assets', function () {
    if ($(this).is(':checked')) {
      $('.js-subsequent-assets-liabilities-numbers').hide();
      $('.js-has-joint-assets-state').val('subsequent');
    } else {
      $('.js-subsequent-assets-liabilities-numbers').show();
      $('.js-has-joint-assets-state').val('');
    }
  });
  $('#has_joint_assets').trigger('change');

  // Consent & Submit
  $(document).on('click', '.submitApplication', function () {
    const $this = $(this);

    const curStep = $(this).closest('.setup-content');
    const curStepBtn = curStep.attr('id');
    const nextStepWizard = $(`.setup-panel div a[href="#${curStepBtn}"]`)
      .parent()
      .next()
      .children('a');

    const data = {
      email: $('#email').val(),
      mobile: $('#work_phone').val(),
      privacyType: $('input[name="privacy_consent_type"]:checked').val(),
      application_id: $('.application_id').val()
    };

    window.httpPost('/applications/sendOtp', data, {
      success(response) {
        if (response.flash === 'success') {
          handleAddEventSmartlook('Completed application', parseInt(response.application_type), startTimeCreateCommercialStep);
          $('.ApplicationWizard').hide();
          if (typeof response.status === 'undefined') {
            window.location.href = '/';
          } else {
            window.location.href = response.redirect_uri;
          }
        } else if (response.flash === 'invalid') {
          notifyBox(
            'Unable to save',
            'Please fill individual information first',
            'error'
          );
        } else if ($this.hasClass('continueAfterSaving')) {
          notifyBox(
            'Error',
            'Unable to save Privacy Consent, Validation(s) fail',
            'error'
          );
        } else {
          notifyBox('Error', 'Error occurred', 'error');
        }
      },
      error(response, status, error) {
        if (ajaxErrorHandler(response.status)) {
          return;
        }
        notifyBox('Error', 'Error occurred', 'error');
      }
    });
  });

  // Calculation of Assets - Stage 3 : Individual Info
  $('.js-assets-sum')
    .on('keyup change', function () {
      const $this = $(this);
      calculateSum($this);
    })
    .trigger('change');

  // Calculation of  Liabilities - Stage 3 : Individual Info
  $('.js-liabilities-sum')
    .on('keyup change', function () {
      const $this = $(this);
      calculateSum($this);
    })
    .trigger('change');

  // Add extra phone number rows
  $(document).on('click', '.addMorePhone', function () {
    const index = `${parseInt(
      $(this)
        .closest('form')
        .attr('data-individual-index')
    )}_${$(this)
        .closest('.phone-number-block')
        .find('.div-phone').length
      }`;
    const repeatElement = $('.clonePhoneNumber')
      .html()
      .replace(/_index/g, index)
      .replace(/%%index%%/g, index);
    $(this)
      .closest('.phone-number-block')
      .find('.phone-number-clone-target')
      .append(repeatElement);
  });

  $(document).on('click', '.removePlaceholder', function () {
    $(this)
      .closest('div')
      .find('label')
      .css('display', 'none');
  });

  // Business Info - No. Partners/Directors dynamically setting on Label
  const howManyDirectorsFieldset = $('.partnersDiv');
  const acnNumberFieldset = $('.acn_number');
  const howManyDirectorsLabel = $('#no_partners');
  $('#applicants_count').prop('disabled', false);

  $('#applicant_type').on('change', function () {
    $('.acn_number').hide();

    switch (parseInt($(this).val())) {
      case APPLICANT_TYPE_INDIVIDUAL:
        howManyDirectorsFieldset.hide();
        break;
      case APPLICANT_TYPE_PARTNERSHIP:
        howManyDirectorsFieldset.show();
        howManyDirectorsLabel.html(
          '<b>How many partners does the partnership have?</b>'
        );
        break;
      case APPLICANT_TYPE_COMPANY:
        howManyDirectorsFieldset.show();
        howManyDirectorsLabel.html(
          '<b>How many directors does the company have?</b>'
        );
        break;
      case APPLICANT_TYPE_TRUST:
        howManyDirectorsFieldset.show();
        howManyDirectorsLabel.html(
          '<b>How many directors does the trustee company have?</b>'
        );
        acnNumberFieldset.show();
        break;
    }
  });

  // Date of Birth
  bindDobInputs();

  // Business Address: manually enter address option
  $('input[name="enter_address_manually"]').click(function () {
    if (this.checked) {
      $('#address').val('');
      $('#address').prop('disabled', true);
      $('#business_address').show();
    } else {
      $('#business_address textarea').val('');
      $('#address').val('');
      $('#address').prop('disabled', false);
      $('#business_address').hide();
    }
  });

  // House Ownership and landlord information
  $(document).on('input', '.house_ownership', function () {
    if (parseInt($(this).val()) === OWNERSHIP_RENTS) {
      $(this)
        .closest('.individual-property-container ')
        .find('.landLordsDiv')
        .show();
    } else {
      $(this)
        .closest('.individual-property-container')
        .find('.landLordsDiv')
        .hide();
    }
    if (parseInt($(this).val()) === OWNERSHIP_OWNS_OUTRIGHT || parseInt($(this).val()) === OWNERSHIP_MORTGAGE) {
      $(this)
        .closest('.individual-property-container')
        .find('.individual-property-details')
        .show();
      if (parseInt($(this).val()) === OWNERSHIP_OWNS_OUTRIGHT) {
        $(this)
          .closest('.individual-property-container')
          .find('.mortgage-value-wrapper').addClass('not_serialize').hide();
      } else {
        $(this)
          .closest('.individual-property-container')
          .find('.mortgage-value-wrapper').removeClass('not_serialize').show();
      }
    } else {
      $(this)
        .closest('.individual-property-container')
        .find('.individual-property-details')
        .hide();
    }
  });

  // Trigger manual postal address field on page load if needed
  $('.house_ownership').each(function () {
    if (parseInt($(this).val()) === OWNERSHIP_RENTS && $(this).prop('checked')) {
      $(this).trigger('input');
    }
  });

  $(document).on('click', '.scrollTopBtn', () => {
    // Review Stage taking each stage to Top to page
    $('html, body').animate(
      {
        scrollTop: 0
      },
      100
    );
  });

  // Multiple Google Maps - Address
  $('#address').each(function (idx, address) {
    const $this = $(address);
    const autocomplete = getGoogleMapsAutoComplete($(this)[0]);

    $this.on('keyup', addressResetField);
    $this.on('change', addressValidation);
    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const place = autocomplete.getPlace();

      $('#business_address_json').val(JSON.stringify(place.address_components));
      $('#business_location_name').val(place.name);
      $('#business_location').val(place.formatted_address);
      $('#business_latitude').val(place.geometry ? place.geometry.location.lat() : '');
      $('#business_longitude').val(place.geometry ? place.geometry.location.lng() : '');
      addressValidation.call($this);
      window.setTimeout(() => {
        $('#business_location_as_entered').val($(address).val());
      }, 100);
    });
  });

  // runs the review validation on page load only if page is review
  window.sfDocumentReady(['route-application-edit'], async () => {
    await runReviewPageValidation();
    handleDisplayFormDetails();
  });

  // records the url anchor in a hidden input element
  // used for running the next(save) button of recorded page anchor via sidebar
  // nav
  const currentNavBarHashElement = $('#current_nav_bar_hash');
  $(document).ready(() => {
    currentNavBarHashElement.val(window.location.hash);
  });

  /**
   * Runs the review page validation before executing navigation via sidebar
   * a jquery selector
   *
   * @return void
   */
  $('.stepwizard-step')
    .children('a')
    .on('click', async () => {
      startTimeCreateCommercialStep = Date.now();
      const currentNavBarHash = currentNavBarHashElement.val();
      switch (currentNavBarHash) {
        case '#basic':
          await runReviewPageValidation();
          break;
        case '#business':
          await runReviewPageValidation();
          break;
        case '#individual1':
        case '#individual2':
        case '#individual3':
        case '#individual4':
          await runReviewPageValidation();
          break;
        case '#product':
          await runReviewPageValidation();
          break;
      }

      // set the new nav bar anchor hash after changing page
      currentNavBarHashElement.val(window.location.hash);
    });

  // Review Page edit link, trigger click to sidebar step wizard
  $('.reviewEditLink').click(function () {
    const section = $(this).attr('href');
    $('.stepwizard-step')
      .children(`a[href="${section}"]`)
      .trigger('click');
  });

  /**
   * Runs the Review page summary validation
   * when URL hash is review page
   *
   * @return {Promise<void>}
   */
  async function runReviewPageValidation() {
    if (window.location.hash === '#review') {
      // validate pages when visiting the Review Page
      await startReview();
      // Review Page edit link, trigger click to sidebar step wizard
      $('.edit-stage-link').on('click', function () {
        const section = $(this).attr('href');
        $('.stepwizard-step')
          .children(`a[href="${section}"]`)
          .trigger('click');
      });
    }
  }

  /**
   * Review page. Collect information and update review page
   */
  async function startReview() {
    const data = {
      app_id: $('.application_id').val()
    };
    const applicationType = parseInt($('#application_type').val());
    if ([APPLICATION_RESIDENTIAL, APPLICATION_CAR].includes(applicationType)) return;
    await window.httpPost('/validate', data, {
      success(response) {
        if (!response.data) {
          return;
        }
        const imgComplete = '/images/icons/check-circle-1.svg';
        const imgIncomplete = '/images/icons/alert-circle.svg';
        const application = response.data.application
        let completeAll = true;
        $.each(response.data.validate, (key, value) => {
          // Complete/incomplete with icon indicators
          if (value.status === 'success') {
            $(`#${key}`).find('.status').html('Complete');
            $(`#${key}`).find('.review-image .image').html(
              `<img src="${imgComplete}" class="image" height="20">`
            );
            $(`#${key}`).find('.description-stage').html(value.msg);

            $(`#${key}`).find('.edit-stage-link').html('Edit');
            $(`#${key}`).find('.edit-stage-link').removeClass('button btn btn-submit');
          } else {
            $(`#${key}`).find('.status').html('Incomplete');
            $(`#${key}`).find('.review-image .image').html(
              `<img src="${imgIncomplete}" class="image" height="20">`
            );
            $(`#${key}`).find('.description-stage').html(value.msg);
            $(`#${key}`).find('.edit-stage-link').addClass('button btn btn-submit');
            $(`#${key}`).find('.edit-stage-link').html('Complete this step');
            if (!application.privacy_consent_type)
              $('#resStage_review').find('.description-stage').html(`
                <div class="mb-4">To proceed will need to sign a privacy consent.</div>
                <div>
                This will allow Solaris Finance to handle their information for the purposes of assessing
                a loan application. Further information will be provided on the document.
                </div>
              `);
            if (key !== 'resStage_review') {
              completeAll = false;
            }
          }
        });
        if (!completeAll) {
          $('#resStage_review').find('.description-stage').html("Please complete all the above steps before providing privacy consent.");
          $('#consent-form-container').hide();
          $('.consent-container').hide();
          $('.submitApplication').prop('disabled', true);
        } else {
          const individualsEmails = application.applicants.map(item => item.email)
          if (!application.privacy_consent_type)
            $('#consent-form-container').show();
          $('.consent-container').show();
          $('.submitApplication').prop('disabled', false);
          $('#privacy-text-digital-emails').html(individualsEmails.join(', '))
        }
        $('#resStage_review').find('.edit-stage-link').hide();
      },
      error(response, status, error) {
        $('.submitApplication').prop('disabled', true);
        if (ajaxErrorHandler(response.status)) {
          return;
        }
        notifyBox(
          'Error',
          'Please check that all steps of the application are complete before reviewing the application.',
          'error'
        );
      }
    });
  }

  /**
   * This function is used to add another stage to the application form
   * @param applicantsCount - the number of applicants
   * @param stage - the stage of the application (e.g. 'applicant', 'employment', 'income', 'assets',
   * 'liabilities')
   * @param deepClone - boolean
   */
  function handleAddOneStage(applicantsCount, stage, deepClone) {
    if (parseInt(applicantsCount) === 2) {
      if (!$(`#${stage}-content-2`).length) {
        const $elementCloned = $(`#${stage}-content-1`).clone(deepClone);
        const $navItemCloned = $(`.stepwizard-step#stepwizard-${stage}1`).first().clone(true)
        const $reviewItemCloned = $(`.review-application #resStage_${stage}1`).clone(true)
        const applicationType = $elementCloned.find('#application_type').val()
        $elementCloned.find('.ffl-wrapper').floatingFormLabels({
          formElements: 'input, textarea, select'
        });
        $elementCloned.attr('id', `${stage}-content-2`);
        $elementCloned.attr('data-stage', `${stage}2`)
        $elementCloned.find('form').attr(`data-${stage}-index`, '2')
        $elementCloned.find('input[type=hidden]:not(.application_id, .finance_type, .income_type_value, .no-change-value)').val('')
        $elementCloned.find('input[type=hidden][name=stage]').val(`${stage}2`)
        $elementCloned.find('input[type=radio]').prop('checked', false);
        $elementCloned.find('.work_phone_type').prop('checked', true);
        $elementCloned.find('option:selected').prop('selected', false);
        $elementCloned.find('input[type=text]').val('');
        $elementCloned.find('input[type=contact_email]').val('');
        $elementCloned.find('input[type=number]').val('');
        $elementCloned.find('#application_type').val(applicationType);
        $elementCloned.find('input[type=radio], input[type=checkbox], input[type=text], input[type=contact_email]').each(function () {
          const oldId = $(this).attr('id');
          $(this).attr('id', `${stage}2-${oldId}`)
          $(this).siblings('label').attr('for', `${stage}2-${oldId}`)
        });
        $elementCloned.find('.container_detail').hide()
        $elementCloned.hide()
        $elementCloned.insertAfter(`#${stage}-content-1`)
        //clone nav step
        $navItemCloned.removeClass('success step-complete')
        $navItemCloned.attr('id', `stepwizard-${stage}2`)
        $navItemCloned.find('a').attr('href', `#${stage}2`)
        $navItemCloned.find('h6').html(`${stage} 2`)
        $(`.stepwizard-step#stepwizard-${stage}1 h6`).html(`${stage} 1`)
        $navItemCloned.insertAfter(`.stepwizard-step#stepwizard-${stage}1`)
        //clone review
        $reviewItemCloned.attr('id', `resStage_${stage}2`);
        $reviewItemCloned.find('.review-title h4').html(`${stage} 2`);
        $reviewItemCloned.find('a.edit-stage-link').attr('href', `#${stage}2`)
        $reviewItemCloned.insertAfter(`.review-application #resStage_${stage}1`)
      }
    } else if (parseInt(applicantsCount) === 1 && $(`#${stage}-content-2`).length) {
      $(`#${stage}-content-2`).remove()
      $(`.stepwizard-step#stepwizard-${stage}2`).remove()
      $(`.review-application #resStage_${stage}2`).remove()
      $(`.stepwizard-step#stepwizard-${stage}1 h6`).html(`${stage}`)
    }
    handleReStepWizard()
    changeTitleIncome()
    // hides and shows content for newly appended pages
    $('.setup-panel div a').on('click', function (e) {
      sidebarNavDisplay(e, $(this), $('.setup-panel div a'), $('.setup-content'))
    });
    setAutocompleteOnAddresses()
    handleAddAnotherProperty()
    addressInput()
    bindDobInputs()
  }

  // Move to the stage indicated by the URL
  // ran only once during page load
  switch (window.location.hash) {
    case '#basic':
    case '#business':
    case '#individual':
    case '#commitments':
    case '#income1':
    case '#income2':
    case '#expenses':
    case '#assets':
    case '#uploads':
    case '#individual1':
    case '#individual2':
    case '#individual3':
    case '#individual4':
    case '#product':
    case '#review':
    case '#personal':
    case '#installers':
    case '#experience':
    case '#login':
    case '#acknowledgement':
      $(`.setup-panel div a[href="${window.location.hash}"]`).trigger('click');
      break;
    default:
      // Otherwise, move to preconfigured stage
      // This is the basic information page for most applications, and the
      // review page for submitted applications
      $('.setup-panel div a.btn-active').trigger('click');
  }

  setAutocompleteOnAddresses();
  setAutocompleteOnPreviousAddress();
  addressInput();
  handleAddAnotherProperty();
});

// scripts under view application
window.sfDocumentReady([
  'route-application-view',
  'route-application-residential-view',
  'route-supplier-accreditation-edit',
  'route-application-car-view',
  'route-application-stock-finance-edit',
  'route-application-stock-finance-view'
], async () => {
  const ConditionReasonModal = {
    selector: '.condition-reason-modal',
    showReadonly(comment) {
      const modal = $(this.selector);
      const modalCommentBox = modal.find('#condition_comment');
      const saveBtn = modal.find('.js-modal-save');

      saveBtn.hide();
      modalCommentBox.attr('disabled', true);
      if (comment) {
        modalCommentBox.val(comment);
      }

      this.show(null, () => {
        saveBtn.show();
        modalCommentBox.val('');
        modalCommentBox.attr('disabled', false);
      })
    },
    show(onSave, onCancel) {
      const modal = $(this.selector);
      modal.show();
      const modalCommentBox = modal.find('#condition_comment');
      const saveBtn = modal.find('.js-modal-save')
      const cancelBtn = modal.find('.js-modal-cancel,.js-model-close')

      saveBtn.off('click').on('click', () => {
        this.hide();
        typeof onSave === 'function' && onSave(modalCommentBox.val());
      });
      cancelBtn.off('click').on('click', () => {
        this.hide();
        typeof onCancel === 'function' && onCancel();
      });
      modal.removeClass('hidden').removeClass('opacity-0');
    },
    hide() {
      const modal = $(this.selector);
      modal.addClass('hidden').addClass('opacity-0');
    }
  };

  $('.js-comment-caution-btn').click(function () {
    // get the comment of the condition
    const commentTarget = $(this).data('comment-target');
    const comment = $(`#${commentTarget}`).val();

    // show the modal
    ConditionReasonModal.showReadonly(comment);
  });

  const ConditionStatuses = {
    RECEIVED: 1,
    NOT_RECEIVED: 2,
    PARTIALLY_RECEIVED: 3,
    PENDING_REVIEW: 4,
  };
  const ConditionStatusClasses = {
    [ConditionStatuses.RECEIVED]: 'received',
    [ConditionStatuses.NOT_RECEIVED]: 'not-received',
    [ConditionStatuses.PARTIALLY_RECEIVED]: 'partially-received',
    [ConditionStatuses.PENDING_REVIEW]: 'pending-review',
  };

  const shouldShowConditionReason = status => [ConditionStatuses.NOT_RECEIVED, ConditionStatuses.PARTIALLY_RECEIVED].includes(status)
  const updateConditionStatus = (conditionIdentifier, status, comment) => {
    $(`.js-settlement-condition-${conditionIdentifier}`)
      .removeClass(
        'settlement-condition--received settlement-condition--not-received \
        settlement-condition--partially-received settlement-condition--pending-review'
      )
      .addClass(`settlement-condition--${ConditionStatusClasses[status]}`);

    const commentBox = $(`#${conditionIdentifier}`)
      .parent()
      .children('.settlement-condition__comment')
    if (!shouldShowConditionReason(status)) {
      commentBox.addClass('hidden') // hide comment icon
    } else {
      commentBox.removeClass('hidden') // show comment icon

      if (comment) {
        commentBox.children('input').val(comment) // update comment
      }
    }

    $(`#force-condition-status__${conditionIdentifier}`).val(status).data('status', status);
  }

  $('.js-force-condition-status').on('change', function () {
    const select = $(this);
    const indicator = select.parent().find('.indicator');
    const name = select.data('name') || 'condition'
    const cid = select.data('cid')

    const status = parseInt(select.val())
    const data = {
      min_requirement_id: select.data('min_requirement_id'),
      special_condition_id: select.data('special_condition_id'),
      application_id: select.data('application_id'),
      type: select.data('type'),
      name,
      status
    };
    const controls = {
      select,
      indicator
    };

    if (status === ConditionStatuses.PENDING_REVIEW && getConditionDownloadLinks(cid).length === 0) {
      select.val(select.data('status'));
      return alert('Please ensure there are uploaded files before marking this condition as Pending Review');
    }

    if (shouldShowConditionReason(status)) {
      ConditionReasonModal.show(
        comment => // save
          postConditionStatusApi(cid, Object.assign({ comment }, data), controls),
        () => { // cancel
          select.val(select.data('status'));
          select.prop('disabled', false);
          indicator.addClass('hidden');
        }
      )
    } else {
      postConditionStatusApi(cid, data, controls);
    }
  });

  const postConditionStatusApi = (cid, data, controls) => {
    const { indicator, select } = controls
    const { name, status } = data

    // Saving
    select.prop('disabled', true);
    indicator.removeClass('hidden');

    Api.post('conditions/force-status', data)
      .then(res => {
        const comment = _.get(res, 'data.data.comment')

        select.data('status', status);
        updateConditionStatus(cid, status, comment);

        notifyBox('Updated status', `${name} status updated`, 'success');
      })
      .catch(err => {
        const status = _.get(err, 'response.status');
        let errTitle = 'Status not updated';
        let errMessage = _.get(err, 'response.data.message', 'Something went wrong');

        if (status === 504) {
          errTitle = 'Timeout';
          errMessage = 'Please try again later';
        }

        select.val(select.data('status'));
        notifyBox(errTitle, errMessage, 'error');
      })
      .finally(() => {
        // Done
        select.prop('disabled', false);
        indicator.addClass('hidden');
        ConditionReasonModal.hide();
      });
  };

  const getConditionDownloadLinks = cid => {
    const downloadLinkEl = $(`.js-download-condition-link[data-condition-name="${cid}"]`)
    const downloadLinkFilesEl = downloadLinkEl.children('.js-download-files');
    let conditionDownloadLinks = []

    try {
      conditionDownloadLinks = JSON.parse(downloadLinkFilesEl.val()) || []
    } catch (err) { }

    return conditionDownloadLinks;
  }

  /**
   * init form files into dropzone forms
   * using dropzones programatically to set options and
   * use event handlers
   *
   * Also responsible to upload condition files into server and update status
   */
  $('.dropzones').each(function () {
    const $form = $(this).closest('fieldset.form-group')
    let documentType = parseInt($form.find('.document_type').val());
    if(documentType)
      return
    const url = $(this).attr('action');
    $(this).dropzone({
      timeout: 0,
      paramName: 'file',
      maxFilesize: 10,
      acceptedFiles: ".pdf, .jpg, .jpeg, .png .csv",
      url,
      uploadMultiple: true,
      headers: {
        'X-CSRF-TOKEN': $('meta[name="csrf-token"]').attr('content')
      },
      sending: () => {
        $('.js-loading').show();
      },
      sending: (_, xhr, __) => {
        // Handle browser timeout errors
        xhr.ontimeout = function () {
          notifyBox('Error', 'Timeout, please check your internet connection', 'error');
        };
      },
      successmultiple: (_, response) => {
        if (response.data && response.downloadLinks && response.downloadLinks.length) {
          updateConditionStatus(response.cid, response.data.status)
          notifyBox('File uploaded', `Settlement Condition: ${response.data.name}`, 'success');

          const downloadLinkEl = $(`.js-download-condition-link[data-condition-name="${response.cid}"]`)
          const downloadLinkFilesEl = downloadLinkEl.children('.js-download-files');
          let conditionDownloadLinks = []

          try {
            conditionDownloadLinks = JSON.parse(downloadLinkFilesEl.val()) || []
          } catch (err) { }

          const mergedDownloadLinks = conditionDownloadLinks.concat(response.downloadLinks)

          downloadLinkFilesEl.val(JSON.stringify(mergedDownloadLinks))
          downloadLinkEl.children('.js-download-filecount').text(mergedDownloadLinks.length)

          if (mergedDownloadLinks.length) {
            downloadLinkEl.removeClass('hidden')
          }
        } else {
          notifyBox('Error', 'Error occurred, condition file not saved', 'error');
        }
        $('.js-loading').hide();
      },
      error: (file, message, request) => {
        const requestStatus = request && request.status

        if (requestStatus === 422) {
          displayErrorMessage($(this), message.errors)
        } else {
          notifyBox('Error', message, 'error');
        }

        $('.js-loading').hide();
        $(file.previewElement).remove();
      },
    });
  });

  /**
   * Multi download event
   * Creates an anchor element to attach the downloadable files
   */
  $('.js-download-condition-link').on('click', function (e) {
    e.preventDefault();

    const jsDownloadFilesElement = $(this).children('.js-download-files');
    const jsonFiles = jsDownloadFilesElement.val();

    window.downloadLinks(JSON.parse(jsonFiles));
  });

  /**
   * Edit repayments
   */
  $('.js-edit-repayments').on('click', () => $('.application__repayments').addClass('editing'));
  $('.js-cancel-repayments').on('click', () => {
    $('.application__repayments').removeClass('editing')
    $('#custom_include_gst').val(
      $('.repayments__amount .amount__include_gst').text()
        .replace('$', '')
        .replace(',', '')
    );
  });
  $('.js-save-repayments').on('click', async function () {
    const uuid = $('#uuid').val();

    $('.application__repayments input,button').attr('disabled', true);
    $(this).html('Saving');

    try {
      await Api.post(`applications/${uuid}/custom-repayments`, {
        custom_include_gst: $('#custom_include_gst').val()
      });

      location.reload();
    } catch (err) {
      const status = _.get(err, 'response.status');
      const message = _.get(err, 'response.data.message', err.message);

      if (status === 422) {
        const errors = _.get(err, 'response.data.errors', []);

        _.each(errors, (fieldErrors, fieldName) => {
          $(`.${fieldName}-err`).html(_.get(fieldErrors, 0, ''))
        });
      }

      if (!ajaxErrorHandler(status)) {
        notifyBox(`Error (${status})`, message, 'error');
      }

      $('.application__repayments input,button').attr('disabled', false);
    } finally {
      $(this).html('Save');
    }
  })
});
$('.wrapper-input-field').hide()
$('.js-btn-action-field').hide()

function handleHiddenInputFieldsApplication($context) {
  const $container = $context.closest('td')
  $container.find('.wrapper-input-field').hide()
  $container.find('.js-btn-action-field').hide()
  $container.find('.js-field-title').show()
  $container.find(".js-btn-edit-field").show()
}

$(".js-btn-edit-field").on('click', function () {
  const $container = $(this).closest('td')
  $container.find('.wrapper-input-field').show()
  $container.find('.js-btn-action-field').show()
  $container.find('.js-field-title').hide()
  $(this).hide()
})

$('.js-cancel-field').on('click', function () {
  handleHiddenInputFieldsApplication($(this))
})

$('.js-save-job-id').on('click', async function () {
  const $buttonSubmit = $(this)
  const $container = $(this).closest('td')
  const uuid = $('#uuid').val();

  $container.find('.wrapper-field input,button').attr('disabled', true);
  $(this).html('Saving');

  try {
    const response = await Api.put(`applications/${uuid}`, {
      job_id: $('#job_id').val()
    });
    $('#job_id').val(response.data.data.job_id)
    $container.find('.js-field-title span').html(response.data.data.job_id)
    handleHiddenInputFieldsApplication($buttonSubmit)
  } catch (err) {
    const status = _.get(err, 'response.status');
    const message = _.get(err, 'response.data.message', err.message);

    if (status === 422) {
      const errors = _.get(err, 'response.data.errors', []);
      _.each(errors, (fieldErrors, fieldName) => {
        $(`.${fieldName}-err`).html(_.get(fieldErrors, 0, ''))
      });
    }

    if (!ajaxErrorHandler(status)) {
      notifyBox(`Error (${status})`, message, 'error');
    }

  } finally {
    $(this).html('Save');
    $container.find('.wrapper-field input,button').attr('disabled', false);
  }
})

$('.js-save-installation-date').on('click', async function () {
  const $buttonSubmit = $(this);
  const $container = $(this).closest('td');
  const uuid = $('#uuid').val();

  $container.find('.wrapper-field input,button').attr('disabled', true);
  $(this).html('Saving');

  try {
    const response = await Api.put(`applications/${uuid}`, {
      installation_date: $('#installation_date').val()
    });
    $('#installation_date').val(response.data.data.installation_date);
    $container
      .find('.js-field-title span')
      .html(response.data.data.installation_date);
    handleHiddenInputFieldsApplication($buttonSubmit);
  } catch (err) {
    const status = _.get(err, 'response.status');
    const message = _.get(err, 'response.data.message', err.message);

    if (status === 422) {
      const errors = _.get(err, 'response.data.errors', []);
      _.each(errors, (fieldErrors, fieldName) => {
        $(`.${fieldName}-err`).html(_.get(fieldErrors, 0, ''));
      });
    }

    if (!ajaxErrorHandler(status)) {
      notifyBox(`Error (${status})`, message, 'error');
    }
  } finally {
    $(this).html('Save');
    $container.find('.wrapper-field input,button').attr('disabled', false);
  }
});

window.sfDocumentReady(['route-applications', 'route-application-edit', 'route-application-view', 'route-application-residential-view'], function () {
  if (window.smartlook) {
    setEventSmartLookApplication();
  }
  confirmWithdraw();
});

function setEventSmartLookApplication() {
  $('.btn-new-application').click(function () {
    smartlook('track', 'new application');
  });
  $('.sl-edit-application').click(function () {
    smartlook('track', 'edit application');
  });
}

function confirmWithdraw() {
  $('.js-confirm-withdraw').on('click', async function (event) {
    const userConfirmed = confirm(
      'Are you sure you want to withdraw this application?',
    );
    if (!userConfirmed) {
      event.preventDefault();
    }
  });
}

/* The above code is a JavaScript function that is called when the page is loaded. */
window.sfDocumentReady([
  'route-application-residential-edit',
  'route-application-car-edit',
  'route-application-stock-finance-edit',
], () => {
  addressInput();
  handleReStepWizard();
  changeTitleIncome();
  handleOnchangeOtherIncome();
  // Step 2 - Individual Information Saving
  $(document).on('click', '.button-save-applicant', function () {
    saveResidentialIndividual($(this));
  });
  // Step 3 - Loans Information Saving
  $(document).on('click', '.button-save-commitments', function () {
    saveResidentialCommitments($(this));
  });
  // Step 4 - Incomes Information Saving
  $(document).on('click', '.button_save_incomes', function () {
    saveResidentialIncomes($(this));
  });
  // Step 5 - Expenses Information Saving
  $(document).on('click', '.button_save_expenses', function () {
    const rules = EXPENSES_RULES
    const $form = $(this).closest('form')
    const dataForm = $form.serializeArray()

    const isValid = validate(dataForm, $form, rules);
    if (!isValid) return false

    saveResidential($(this)).then((response) => {
      handleAddEventSmartlook('Step expense', parseInt(response.application_type), startTimeCreateCommercialStep)
    });
  });
  // Step 6 - Assets Information Saving
  $(document).on('click', '.button_save_assets', function () {
    const rules = ASSETS_RULES
    const $form = $(this).closest('form')
    const dataForm = $form.serializeArray()

    const isValid = validate(dataForm, $form, rules);
    if (!isValid) return false

    saveResidential($(this)).then((response) => {
      handleAddEventSmartlook('Step assets', parseInt(response.application_type), startTimeCreateCommercialStep)
    })
  });
  // Step 7 - Upload Information Saving
  $(document).on('click', '.button_save_upload', function () {
    const $form = $(this).closest('form')
    const dataForm = $form.serializeArray()
    const rules = handleUploadResidentialRules(dataForm, $form);

    const isValid = validate(dataForm, $form, rules);
    if (!isValid) return false

    saveResidential($(this)).then((response) => {
      handleAddEventSmartlook('Step upload', parseInt(response.application_type), startTimeCreateCommercialStep)
    });
  })
});

function handleOnchangeOtherIncome() {
  $('input.income-status').on('change', function () {
      var $fieldset = $(this).closest('fieldset.other-income');
      var isIncomeSelected = parseInt($(this).val()) === 1;
      $fieldset.toggleClass('not_serialize', !isIncomeSelected);
  });
}

/**
 * It saves the form data to the database
 * @param  - The context of the form.
 * @param [dataForm=null] - The form data to be sent to the server.
 * @returns A function that takes two parameters,  and dataForm.
 */
const saveResidential = ($context, dataForm = null) => {
  if ($('.application_id').val()) {
    if (!dataForm) {
      const $form = $context.closest('form');
      if (isDirty($form) && !confirmPrivacyNullification()) {
        $form.trigger('reset');
        return;
      }
      dataForm = $form.serializeArray();
    }
    const $curStep = $context.closest('.setup-content');
    const curStepBtn = $curStep.attr('data-stage');
    const navItem = $(`.setup-panel div a[href="#${curStepBtn}"]`).parent();
    const nextStepWizard = navItem.next().children('a');
    return new Promise((resolve, reject) => {
      saveApplication(dataForm, {
        success(res) {
          const data = res.data

          $('.js-display-errors').html('');
          if (data.status === 'success') {
            navItem.addClass('step-complete');
            if ($context.hasClass('continueAfterSaving')) {
              nextStepWizard.trigger('click');
            } else {
              // redirect to listing on save & exit
              window.location.href = '/';
            }
            $('html, body').animate(
              {
                scrollTop: 0
              },
              100
            );
          }
          $('html, body').animate(
            {
              scrollTop: 0
            },
            100
          );
          resolve(data);
        },
        error(response, status, error) {
          if (response.responseJSON && response.responseJSON.errors) {
            const errors = response.responseJSON.errors;
            displayErrorMessage($context, errors, response.status)
          } else {
            notifyBox('Error', 'An unexpected error occurred. Please try again', 'error');
          }
          if (ajaxErrorHandler(response.status)) {
            return;
          }
          navItem.removeClass('step-complete');
        }
      })
    });
  } else {
    notifyBox(
      'Unable to save',
      'Please fill basic information first',
      'error'
    );
  }
}


/**
  * Saving Residential Individual Information
  *
  * This is used both by save next button and side bar nav
  * @param $this
  * @return {Promise<void>}
*/
const saveResidentialIndividual = $context => {
  const $form = $context.closest('form');
  const dataForm = $form.serializeArray();
  const rules = { ...INDIVIDUAL_RULES, ...INDIVIDUAL_RESIDENTIAL_RULES };
  if (!$form.find('input[name="living_from_years"]').val()) {
    rules['living_from_months'] = {
      required: {
        value: true, message: 'Required'
      },
      max: {
        value: 12, message: 'Value should be between 1 to 12 months'
      },
      min: {
        value: 1, message: 'Value should be between 1 to 12 months'
      }
    }
  } else {
    rules['living_from_months'] = {}
  }

  const isValid = validate(dataForm, $form, rules);
  if (!isValid) return false;

  if (isDirty($form) && !confirmPrivacyNullification()) {
    $form.trigger('reset');
    return;
  }

  dataForm.push({
    name: "phone[0][work_phone]",
    value: $form.find('.work_phone').val().replace(/\s/g, '')
  },
    {
      name: "phone[0][work_phone_type]",
      value: 1
    });
  dataForm.push({ name: 'application_id', value: $('.application_id').val() });
  saveResidential($context, dataForm).then(res => {
    handleAddEventSmartlook('Step individual', parseInt(res.application_type), startTimeCreateCommercialStep)
    $context
      .closest('form')
      .find('.applicant_id')
      .val(res.applicant_id);
    if (res.stage === 'individual1') {
      $('#income-content-1').find('input.individual_id').val(res.applicant_id)
    } else if (res.stage === 'individual2') {
      $('#income-content-2').find('input.individual_id').val(res.applicant_id)
    }
  })
}


/**
 * Saving Residential Commitments Information
 *
 * It saves the form data to the database
 * @param  - The button that was clicked.
 */
const saveResidentialCommitments = $context => {
  const $form = $context.closest('form');
  const dataForm = $form.serializeArray();
  const rules = handleCommitmentRules()

  const isValid = validate(dataForm, $form, rules);
  if (!isValid) return false;

  if (isDirty($form) && !confirmPrivacyNullification()) {
    $form.trigger('reset');
    return;
  }
  const childrenInfoSerialize = $('#js-children-info :input').serializeArray()
  const childrenInfoData = [];
  childrenInfoSerialize.forEach(element => {
    childrenInfoData.push({ 'age': element.value });
  });
  dataForm.push({
    name: 'children_info',
    value: JSON.stringify(childrenInfoData)
  })

  saveResidential($context, dataForm).then(res => {
    handleAddEventSmartlook('Step commitment', parseInt(res.application_type), startTimeCreateCommercialStep)
    const loanStatus = res.data.loanStatus;
    $context.closest('form').find('.application_commitment_id').each(function (index) {
      if (loanStatus[index]) {
        $(this).val(loanStatus[index].id)
        if (loanStatus[index].isDeleted) {
          $(this).closest('.commitment-item').remove();
        }
      }
    })
  })
}

function handleCommitmentRules() {
  const rules = LOAN_RULES;

  if (Number($('#number_children').val()) > 0) {
    rules['children'] = {
      required: {
        value: true, message: 'Required'
      },
    }
  }

  function updateLoanFieldsRules($loanStatusInput, index, fields, rules) {
    if (parseInt($loanStatusInput.val()) === 1) {
      fields.forEach(field => {
        rules[`loans[${index}][${field}]`] = generateRequiredRule();
      });
    } else {
      fields.forEach(field => {
        delete rules[`loans[${index}][${field}]`]
      });
    }
  }

  $('.commitment-wrapper .commitment-item').each(function () {
    const fields = ['lender', 'property_value', 'balance_outstanding', 'min_repayment', 'frequency', 'loan_type']
    const $loanStatusInput = $(this).find(`.loan-status:checked`);
    const nameAttribute = $(this).find('.loan-status').attr('name');
    const match = nameAttribute.match(/\[(\d+)\]/);
    if (match) {
      const index = match[1];
      updateLoanFieldsRules($loanStatusInput, index, fields, rules)
    }
  })

  return rules
}

/**
 * Saving Residential Incomes Information
 *
 * It saves the form data to the database
 * @param  - The button that was clicked.
 */
const saveResidentialIncomes = $context => {
  const $form = $context.closest('form');
  const dataForm = $form.find("input, select").not(".not_serialize input, .not_serialize select").serializeArray();
  const rules = handleIncomeRules(dataForm, $form)

  const isValid = validate(dataForm, $form, rules);
  if (!isValid) return false;

  if (isDirty($form) && !confirmPrivacyNullification()) {
    $form.trigger('reset');
    return;
  }

  saveResidential($context, dataForm).then(res => {
    handleAddEventSmartlook('Step incomes', parseInt(res.application_type), startTimeCreateCommercialStep)
    const incomesStatus = res.data.incomesStatus;
    const $inputIncomeIds = $context.closest('form').find('.row:first').children('fieldset:not(.not_serialize)').find('.application_income_id')
    $inputIncomeIds.each(function (index) {
      if (incomesStatus[index]) {
        $(this).val(incomesStatus[index].id);
        if (incomesStatus[index].isDeleted) {
          $(this).closest('.other-income').remove();
        }
      }
    })
  })
}

function handleIncomeRules(dataForm, $form) {
  const rules = INCOME_RULES
  const getValue = (name) => {
    const data = dataForm.find(item => item.name === name)
    return data ? data.value : null
  }

  $form.find('.panel-body .row').children('fieldset').each(function (index) {
    const nameField = $(this).find('input').attr('name')
    if (!nameField) return rules
    const indexName = nameField.match(/\[(\d+)\]/)[1];
    if (dataForm.find(item => item.name === `incomes[${indexName}][status]` && item.value === '1')) {
      rules[`incomes[${indexName}][job_type]`] = generateRequiredRule()
      const incomeType = Number(getValue(`incomes[${indexName}][job_type]`))

      if (incomeType === INCOME_INVESTMENT_PROPERTY) {
        rules[`incomes[${indexName}][business_address]`] = generateRequiredRule()
      }

      if (INCOME_PERMANENT_TYPES_GROUP.includes(incomeType)) {
        rules[`incomes[${indexName}][job_title]`] = generateRequiredRule()
        rules[`incomes[${indexName}][employer_name]`] = generateRequiredRule()
        rules[`incomes[${indexName}][employer_address]`] = generateRequiredRule()
      }

      if (incomeType === INCOME_TYPE_SELF_EMPLOYED) {
        rules[`incomes[${indexName}][abn_number]`] = generateRequiredRule()
        rules[`incomes[${indexName}][business_name]`] = generateRequiredRule()
        rules[`incomes[${indexName}][business_address]`] = generateRequiredRule()
      }

      if (incomeType === INCOME_TYPE_SELF_EMPLOYED && parseInt(getValue(`incomes[${indexName}][applicant_type_id]`)) === APPLICANT_TYPE_TRUST) {
        rules[`incomes[${indexName}][acn_number]`] = generateRequiredRule()
        rules[`incomes[${indexName}][business_acn_name]`] = generateRequiredRule()
      }

      if (getValue(`incomes[${indexName}][working_from_year]`) === '') {
        rules[`incomes[${indexName}][working_from_month]`] = generateRequiredRule()
      } else {
        rules[`incomes[${indexName}][working_from_month]`] = {}
      }
    }
  })

  return rules
}

function handleUploadResidentialRules(dataForm, $form) {
  const rules = { ...UPLOAD_DOCUMENT_RULES };

  const formatToNumber = (value) =>
    Number(value.replace(/[^0-9.]+/g, "")) || 0;

  const systemCost = formatToNumber($('#system_cost').val());

  $form.find('.panel-body .row fieldset').each(function () {
    const $input = $(this).find('input[name]');
    const nameField = $input.attr('name');
    if (!nameField) return;

    const indexMatch = nameField.match(/\[(\d+)\]/);
    if (!indexMatch) return;

    const indexName = indexMatch[1];
    const statusField = `application_documents[${indexName}][status]`;
    const fileNameField = `application_documents[${indexName}][file_name]`;

    const isStatusApproved =
      dataForm.some(
        (item) => item.name === statusField && item.value === '1'
      );

    rules[fileNameField] = isStatusApproved ? generateRequiredRule() : {};
    if (systemCost < MINIMUM_BANK_STATEMENT && parseInt($("#application_type").val()) !== APPLICATION_STOCK_FINANCE) {
      rules['status'] = '';
    }
  });

  return rules;
}

function handleIndividualCommercialRules(dataForm, $form) {
  const getValue = (name) => {
    const data = dataForm.find(item => item.name === name)
    return data ? data.value : null
  }
  const rules = { ...INDIVIDUAL_RULES, ...INDIVIDUAL_COMMERCIAL_RULES };
  if (!$form.find('input[name="living_from_years"]').val()) {
    rules['living_from_months'] = {
      required: {
        value: true, message: 'Required'
      },
      max: {
        value: 12, message: 'Value should be between 1 to 12 months'
      },
      min: {
        value: 1, message: 'Value should be between 1 to 12 months'
      }
    }
  } else {
    rules['living_from_months'] = {}
  }

  $form.find('.individual-property-container').each(function (index) {
    const indexName = $(this).find('input').attr('name').match(/\[(\d+)\]/)[1];
    if (dataForm.find(item => item.name === `properties[${indexName}][status]` && item.value === '1')) {
      rules[`properties[${indexName}][property_type]`] = generateRequiredRule()

      if ([OWNERSHIP_OWNS_OUTRIGHT, OWNERSHIP_MORTGAGE].includes(Number(getValue(`properties[${indexName}][property_type]`)))) {
        rules[`properties[${indexName}][value]`] = generateRequiredRule()
      }

      if (Number(getValue(`properties[${indexName}][property_type]`)) === OWNERSHIP_MORTGAGE) {
        rules[`properties[${indexName}][amount_mortgage]`] = generateRequiredRule()
      }

      if (Number(getValue(`properties[${indexName}][property_type]`)) === OWNERSHIP_RENTS) {
        rules[`properties[${indexName}][landlord_name]`] = generateRequiredRule()
        rules[`properties[${indexName}][landlord_phone]`] = generateRequiredRule()
      }
    }
  })

  return rules
}

function handleProductRules(dataForm, $form) {
  const rules = PRODUCT_RULES
  const getValue = (name) => {
    const data = dataForm.find(item => item.name === name)
    return data ? data.value : null
  }

  if ([PRODUCT_STATUS_USED, PRODUCT_STATUS_DEMO].includes(Number(getValue('product_status')))) {
    rules[`product_year`] = generateRequiredRule()
  }
  $form.find('.js-product').each(function (index) {
    const indexName = $(this).find('input').attr('name').match(/\[(\d+)\]/)[1];
    if (Number(getValue('product_detail_fulfillment')) === PRODUCT_DETAIL_ALL) {
      rules[`products[${indexName}][product_name]`] = generateRequiredRule()
      rules[`products[${indexName}][product_type]`] = generateRequiredRule()
      rules[`products[${indexName}][product_qty]`] = generateRequiredRule()
    }
    else if (Number(getValue('product_detail_fulfillment')) === PRODUCT_DETAIL_PARTIAL) {
      rules[`products[${indexName}][product_name]`] = {}
      rules[`products[${indexName}][product_type]`] = {}
      rules[`products[${indexName}][product_qty]`] = {}
    }
  })

  return rules
}

/**
 * It adds a new property to the list of properties, and then adds an event listener to the radio
 * buttons in the new property
 */
export const handleAddAnotherProperty = () => {
  $('.another_property').on('change', function () {
    const $inputRadio = $(this);
    const nameRadio = $inputRadio.attr('name');
    const currentIndex = nameRadio.match(/\[(\d+)\]/)[1];
    const $lastOtherContainer = $inputRadio.closest('.individual-property-container');
    const newIndex = Date.now();
    if (this.value === '1') {
      const $nextProperty = $(this).closest('.individual-property-container').next('.individual-property-container:first');
      if (!$nextProperty.length) {
        const $otherPropertyCloned = $lastOtherContainer.clone(true);
        changeNameOfInput($otherPropertyCloned, currentIndex, newIndex);
        $otherPropertyCloned.find('.input-data:not(input:radio)').val('');
        $otherPropertyCloned.find('.individual_property_id').val('');
        $otherPropertyCloned.find('.property_status').val(1);
        $otherPropertyCloned.find('input[type=radio]').each(function () {
          const oldId = $(this).attr('id');
          $(this).attr('id', `${oldId}_${newIndex}`)
          $(this).siblings('label').attr('for', `${oldId}_${newIndex}`)
          $(this).prop('checked', false);
        })
        $otherPropertyCloned.find('.individual-property-details').hide();
        $otherPropertyCloned.find('.js-display-another-property').remove();
        $otherPropertyCloned.find('.property-location').removeClass('collapse');
        $otherPropertyCloned.insertAfter($(this).closest('form').find('.individual-property-container:last'));
        $otherPropertyCloned.find('.js-display-errors').html('');
      }
      else {
        $nextProperty.show();
        $nextProperty.find('.property_status').val(1);
      }
    }
    if (this.value === '0') {
      const $nextProperty = $(this).closest('.individual-property-container').next('.individual-property-container:first');
      $nextProperty.find('.property_status').val(0);
      $nextProperty.remove();
    }
    addressInput()
  });
}

/**
 * It takes a container, the current index of the container, and the new index of the container, and
 * then it changes the name of the input fields in the container to the new index
 * @param  - the container of the input field
 * @param currentIndex - the index of the current row
 * @param newIndex - the new index of the row
 * @returns The  is being returned.
 */
function changeNameOfInput($container, currentIndex, newIndex) {
  $container.find('.input-data').each(function () {
    const $input = $(this);
    let nameInput = $input.attr('name');
    const nameField = nameInput.substring(nameInput.lastIndexOf('['));
    nameInput = `properties[${newIndex}]${nameField}`;
    $input.attr('name', nameInput)
    // set class for error message
    const nameFieldNameError = nameField.substring(
      nameField.indexOf('[') + 1,
      nameField.lastIndexOf(']')
    );
    const $messageError = $input.closest('.ffl-wrapper').find('.js-display-errors');
    $messageError.removeClass(`properties_${currentIndex}_${nameFieldNameError}-err`);
    $messageError.addClass(`properties_${newIndex}_${nameFieldNameError}-err`);
  })
  $(`<input name="properties[${newIndex}][property_type]" class="clone" value="4" type="hidden">`).insertAfter($container.find('.property_status'));
  return $container;
}

/**
 * It loops through each stepwizard-step class and finds the anchor tag inside of it and replaces the
 * anchor tag's html with the index of the stepwizard-step class
 */
function handleReStepWizard() {
  $('.stepwizard-step').each(function (index) {
    $(this).find('a').html(index + 1)
  })
}

/**
 * If the page has an element with the id of income-content-2, then for each label in the
 * income-wrapper div, change the text to "Individual 1's primary source of income" and so on
 */
function changeTitleIncome() {
  if ($(`#income-content-2`).length) {
    $('.income-wrapper').each(function (index) {
      $(this).find('.js-primary-income:first .select-primary-source label b').html(`Individual ${index + 1}'s primary source of income`);
    })
  }
}

window.sfDocumentReady([
  'route-application-view',
  'route-application-residential-view',
  'route-application-car-view',
  'route-application-stock-finance-view',
], async () => {
  handleShowAddNewNote()
  handleSubmitNote()
  handleClickEdit()
  handleClickDelete()
  handleMoveToArchive()
  handleCollapseNotes()

  function handleCollapseNotes() {
    $('#collapse_notes').on('show.bs.collapse hide.bs.collapse', function () {
      $('.js-collapse_note_1, .js-collapse_note_2').toggleClass('collapse');
    });
  }

  function handleShowAddNewNote() {
    $('#js-add-new-note, .js-edit-note').on('click', function (event) {
      event.preventDefault()
      clearForm()
      $('#js-form-add-note').show()
    })

    $('.js-btn-cancel').on('click', function (event) {
      event.preventDefault()
      clearForm()
      $(this).closest('.js-wrapper-content').hide()
    })
  }

  function handleSubmitNote() {
    $('#js-save-note').on('click', async function () {
      const $form = $(this).closest('form')
      const dataForm = $form.serializeArray()
      const isValid = validate(dataForm, $form, APPLICATION_NOTES_RULES)
      if (!isValid) return

      const payload = {};
      dataForm.forEach(({ name, value }) => {
        payload[name] = value;
      });

      $form.find('textarea,button').attr('disabled', true);
      $(this).html('Saving...')

      try {
        const noteId = $form.find('input[name="id"]').val()

        if (noteId) {
          await Api.put(`application-notes/${noteId}`, payload);
          location.reload()
          return true
        }

        await Api.post(`application-notes`, payload);
        location.reload()
      } catch (err) {
        const status = _.get(err, 'response.status')
        const message = _.get(err, 'response.data.message', err.message)

        if (status === 422) {
          const errors = _.get(err, 'response.data.errors', [])
          _.each(errors, (fieldErrors, fieldName) => {
            $(`.${fieldName}-err`).html(_.get(fieldErrors, 0, ''))
          });
        }

        if (!ajaxErrorHandler(status)) {
          notifyBox(`Error (${status})`, message, 'error')
        }

        $form.find('textarea,button').attr('disabled', false)
      } finally {
        $(this).html('Save');
      }
    })
  }

  function handleClickEdit() {
    $('.js-edit-note').on('click', async function () {
      const noteId = $(this).closest('.note-item').find('.note-id').val()
      try {
        const response = await Api.get(`application-notes/${noteId}`)
        const data = response.data.data
        const $form = $('#js-form-add-note')
        $form.find('input[name="application_id"]').val(data.application_id)
        $form.find('input[name="user_id"]').val(data.user_id)
        $form.find('textarea[name="notes"]').val(data.notes)
        $form.find('input[name="id"]').val(data.id)
      } catch (err) {
        notifyBox('Error', 'Error occurred, please try again', 'error')
      }
    })
  }

  function handleClickDelete() {
    $('.js-delete-note').on('click', async function (event) {
      event.preventDefault()
      if (confirm('Are you sure you want to delete this note?')) {
        const noteId = $(this).closest('.note-item').find('.note-id').val()

        try {
          const response = await Api.delete(`application-notes/${noteId}`);
          const data = response.data.data
          if (data.id)
            $(this).closest('.note-item').remove()

        } catch (err) {
          notifyBox('Error', 'Error occurred, please try again', 'error')
        }
      }
    })
  }

  function clearForm() {
    const $form = $('#js-form-add-note')
    $form.find('textarea[name="notes"]').val('')
    $form.find('input[name="id"]').val('')
  }

  function handleMoveToArchive() {
    $('.js-btn-archive').on('click', async function (event) {
      const userConfirmed = confirm('Are you sure you want to archive this application?');
      if (!userConfirmed) {
        event.preventDefault();
      }
    });
  }
});

require('./application-list');
require('./application-products');
require('./application-residential');
